import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '../store/auth.store';
import { useTenantContextStore } from '../store/tenant-context.store';

const API_URL = import.meta.env.VITE_API_URL || '';

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Request interceptor to add auth token and tenant context
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add tenant context header for super admin viewing a specific tenant
  const selectedTenant = useTenantContextStore.getState().selectedTenant;
  if (selectedTenant) {
    config.headers['x-tenant-id'] = selectedTenant.id;
  }

  return config;
});

// Singleton refresh promise to prevent concurrent refresh calls
let refreshPromise: Promise<string> | null = null;

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Don't try to refresh for auth endpoints (login, register, etc.)
    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                          originalRequest.url?.includes('/auth/register') ||
                          originalRequest.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      try {
        // If a refresh is already in progress, wait for it instead of firing another one
        if (!refreshPromise) {
          refreshPromise = axios.post(`${API_URL}/api/auth/refresh`, {}, {
            withCredentials: true,
          }).then((response) => {
            const { accessToken } = response.data;
            useAuthStore.getState().setTokens(accessToken);
            return accessToken;
          }).finally(() => {
            refreshPromise = null;
          });
        }

        const accessToken = await refreshPromise;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    // Onboarding enforcement: the backend rejects every data route with 403 until the user
    // has accepted the CGU / configured 2FA. Sync the persisted flags (they may be stale,
    // e.g. enforcement enabled after login) and send the user to the matching forced page.
    if (error.response?.status === 403) {
      const code = (error.response.data as { code?: string } | undefined)?.code;
      const { user, setUser } = useAuthStore.getState();
      if (code === '2FA_NOT_CONFIGURED' && window.location.pathname !== '/setup-2fa') {
        if (user) setUser({ ...user, mustSetup2FA: true });
        window.location.href = '/setup-2fa';
      } else if (code === 'CGU_NOT_ACCEPTED' && window.location.pathname !== '/accept-cgu') {
        if (user) setUser({ ...user, cguAccepted: false });
        window.location.href = '/accept-cgu';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),

  register: (data: {
    tenantName: string;
    tenantSlug: string;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }) => api.post('/auth/register', data),

  logout: () =>
    api.post('/auth/logout', {}),

  me: () => api.get('/auth/me'),

  updateProfile: (data: {
    firstName?: string;
    lastName?: string;
    phone?: string | null;
    notifyEmail?: boolean;
    notifyPush?: boolean;
    notifySms?: boolean;
    language?: string;
  }) => api.patch('/auth/profile', data),

  changePassword: (data: { currentPassword: string; newPassword: string }) =>
    api.post('/auth/change-password', data),

  logoutAll: () => api.post('/auth/logout-all'),

  // 2FA
  setup2FA: () => api.post('/auth/2fa/setup'),
  enable2FA: (secret: string, code: string) => api.post('/auth/2fa/enable', { secret, code }),
  disable2FA: (code: string) => api.post('/auth/2fa/disable', { code }),
  validate2FA: (_userId: string, code: string, mfaToken: string) => api.post('/auth/2fa/validate', { code, mfaToken }),
  get2FAMethods: () => api.get('/auth/2fa/methods'),

  // Tenant settings (admin)
  getTenant: () => api.get('/auth/tenant'),
  updateTenant: (data: {
    companyName?: string | null;
    address?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    postalCode?: string | null;
    country?: string | null;
    phone?: string | null;
    email?: string | null;
    vatNumber?: string | null;
    siret?: string | null;
    website?: string | null;
    billingEmail?: string | null;
    timezone?: string;
    logo?: string | null;
    primaryColor?: string | null;
  }) => api.put('/auth/tenant', data),

  // Forgot / Reset password
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post('/auth/reset-password', { token, password }),

  // CGU/CGV
  acceptCgu: () => api.post('/auth/accept-cgu'),
  getLegalTexts: () =>
    api.get('/auth/legal-texts').then((res) => res.data as { cguText: string; privacyText: string }),

  // Duo 2FA
  enableDuo: () => api.post('/auth/2fa/enable-duo'),
  getDuoAuthUrl: (mfaToken: string, redirectUri: string) =>
    api.post('/auth/duo/auth-url', { mfaToken, redirectUri }),
  duoCallback: (duoCode: string, state: string, redirectUri: string) =>
    api.post('/auth/duo/callback', { duoCode, state, redirectUri }),

  // Passkeys / WebAuthn
  passkeyLoginBegin: (email?: string) =>
    api.post('/auth/passkey/login/begin', { email: email || '' }),
  passkeyLoginFinish: (challengeToken: string, response: unknown) =>
    api.post('/auth/passkey/login/finish', { challengeToken, response }),
  passkeyRegisterBegin: () =>
    api.post('/auth/passkey/register/begin'),
  passkeyRegisterFinish: (challengeToken: string, response: unknown, friendlyName?: string) =>
    api.post('/auth/passkey/register/finish', { challengeToken, response, friendlyName }),
  listPasskeys: () =>
    api.get('/auth/passkeys'),
  deletePasskey: (id: string) =>
    api.delete(`/auth/passkeys/${id}`),
  deleteAllPasskeys: () =>
    api.delete('/auth/passkeys'),
  renamePasskey: (id: string, name: string) =>
    api.put(`/auth/passkeys/${id}`, { name }),
};

// Users API
export const usersApi = {
  list: (params?: { page?: number; limit?: number }) =>
    api.get('/users', { params }),

  get: (id: string) => api.get(`/users/${id}`),

  create: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    role: string;
    sendInvite?: boolean;
  }) => api.post('/users', data),

  update: (id: string, data: Partial<{
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
    role: string;
    isActive: boolean;
  }>) => api.patch(`/users/${id}`, data),

  delete: (id: string) => api.delete(`/users/${id}`),

  setPassword: (id: string, password: string) =>
    api.post(`/users/${id}/set-password`, { password }),

  reset2FA: (id: string) => api.post(`/users/${id}/reset-2fa`),
};

// Devices API
export const devicesApi = {
  list: (params?: {
    status?: string;
    groupId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => api.get('/devices', { params }),

  get: (id: string) => api.get(`/devices/${id}`),

  // Separate from update(): privacy is ADMIN-only server-side and audited
  // under its own entity type.
  updatePrivacy: (
    id: string,
    data: {
      override: boolean;
      enabled: boolean;
      /** WORKING hours keyed by ISO weekday; privacy is their complement. */
      workSchedule: Record<string, { allDay: true } | { start: string; end: string } | null> | null;
      theftOverride: boolean;
    }
  ) => api.put(`/devices/${id}/privacy`, data),

  create: (data: {
    imei?: string;
    // OEM Connect vehicles: no hardware IMEI — the VIN identifies the vehicle.
    oemProvider?: string;
    vin?: string;
    name: string;
    plate?: string;
    brand?: string;
    model?: string;
    speedLimit?: number;
    trackerModel?: string;
    icon?: string;
    markerColor?: string;
    vehicleType?: string;
    avgConsumption?: number;
    evEnabled?: boolean;
    evModelId?: string | null;
  }) => api.post('/devices', data),

  update: (id: string, data: Partial<{
    name: string;
    plate: string;
    brand: string;
    model: string;
    speedLimit: number;
    groupId: string;
    driverId: string | null;
    trackerModel: string;
    icon: string;
    markerColor: string;
    vehicleType: string;
    avgConsumption: number;
    evEnabled: boolean;
    evModelId: string | null;
  }>) => api.put(`/devices/${id}`, data),

  getEnabledParameters: () => api.get('/devices/enabled-parameters'),

  delete: (id: string) => api.delete(`/devices/${id}`),

  setOdometer: (id: string, odometerKm: number) =>
    api.post<{ success: boolean; message: string }>(`/devices/${id}/set-odometer`, { odometerKm }),

  getPositions: (id: string, startDate: string, endDate: string) =>
    api.get(`/devices/${id}/positions`, {
      params: { startDate, endDate },
    }),

  getLatestPosition: (id: string) =>
    api.get(`/devices/${id}/position`),

  sendCommand: (id: string, command: string) =>
    api.post(`/devices/${id}/commands`, { command }),

  getCommands: (id: string) =>
    api.get(`/devices/${id}/commands`),

  getAvailableCommands: () =>
    api.get('/devices/commands/available'),

  // OEM Connect remote control (role-gated backend). Reports 501 until the
  // manufacturer/provider enables the commands capability.
  lockOem: (id: string) => api.post(`/devices/${id}/oem/lock`),
  unlockOem: (id: string) => api.post(`/devices/${id}/oem/unlock`),

  getTrips: (id: string, startDate: string, endDate: string) =>
    api.get(`/devices/${id}/trips`, {
      params: { startDate, endDate },
    }),

  // CSV Import
  importDevices: (devices: Array<{
    imei: string;
    name: string;
    plate?: string;
    brand?: string;
    model?: string;
    year?: number;
    color?: string;
    category?: string;
    speedLimit?: number;
    fuelCapacity?: number;
    fuelConsumption?: number;
    initialOdometer?: number;
    simNumber?: string;
    notes?: string;
  }>) => api.post<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: Array<{ imei: string; error: string }>;
    total: number;
  }>('/devices/import', { devices }),

  getImportTemplate: () => `${api.defaults.baseURL}/devices/import/template`,
};

// Alerts API
export const alertsApi = {
  list: (params?: {
    type?: string;
    status?: string;
    severity?: string;
    deviceId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }) => api.get('/alerts', { params }),

  get: (id: string) => api.get(`/alerts/${id}`),

  getStats: () => api.get('/alerts/stats'),

  acknowledge: (id: string) => api.put(`/alerts/${id}/acknowledge`),

  resolve: (id: string) => api.put(`/alerts/${id}/resolve`),

  bulkAcknowledge: (ids: string[]) => api.put('/alerts/bulk/acknowledge', { ids }),

  bulkResolve: (ids: string[]) => api.put('/alerts/bulk/resolve', { ids }),

  delete: (id: string) => api.delete(`/alerts/${id}`),

  bulkDelete: (ids: string[]) => api.delete('/alerts/bulk', { data: { ids } }),

  // Alert Rules
  listRules: (params?: {
    type?: string;
    isActive?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }) => api.get('/alerts/rules', { params }),

  getRule: (id: string) => api.get(`/alerts/rules/${id}`),

  createRule: (data: {
    name: string;
    type: string;
    conditions: Record<string, unknown>;
    severity?: string;
    isActive?: boolean;
    notifyEmail?: boolean;
    notifyPush?: boolean;
    cooldownMinutes?: number;
    deviceIds?: string[];
    geofenceIds?: string[];
  }) => api.post('/alerts/rules', data),

  updateRule: (id: string, data: Partial<{
    name: string;
    type: string;
    conditions: Record<string, unknown>;
    severity: string;
    isActive: boolean;
    notifyEmail: boolean;
    notifyPush: boolean;
    cooldownMinutes: number;
    deviceIds: string[];
    geofenceIds: string[];
  }>) => api.put(`/alerts/rules/${id}`, data),

  deleteRule: (id: string) => api.delete(`/alerts/rules/${id}`),

  // Bulk rule operations
  bulkDeleteRules: (ids: string[]) => api.delete('/alerts/rules/bulk', { data: { ids } }),
  bulkToggleRules: (ids: string[], isActive: boolean) => api.put('/alerts/rules/bulk/toggle', { ids, isActive }),
  bulkUpdateRecipients: (ids: string[], recipientUserIds: string[]) => api.put('/alerts/rules/bulk/recipients', { ids, recipientUserIds }),

  // Test notification
  testNotification: (data: {
    type: 'email' | 'sms' | 'push' | 'whatsapp';
    alertType?: string;
  }) => api.post<{ success: boolean; message: string; preview?: any }>('/alerts/test-notification', data),
};

// Geofences API
export const geofencesApi = {
  list: (params?: {
    type?: string;
    category?: string;
    isActive?: boolean;
    search?: string;
    page?: number;
    limit?: number;
  }) => api.get('/geofences', { params }),

  get: (id: string) => api.get(`/geofences/${id}`),

  create: (data: {
    name: string;
    type: 'CIRCLE' | 'POLYGON' | 'CORRIDOR';
    color?: string;
    centerLatitude?: number;
    centerLongitude?: number;
    radius?: number;
    coordinates?: [number, number][];
    corridorWidth?: number;
    category?: string;
    isActive?: boolean;
  }) => api.post('/geofences', data),

  update: (id: string, data: Partial<{
    name: string;
    color: string;
    centerLatitude: number;
    centerLongitude: number;
    radius: number;
    coordinates: [number, number][];
    corridorWidth: number;
    category: string;
    isActive: boolean;
  }>) => api.put(`/geofences/${id}`, data),

  delete: (id: string) => api.delete(`/geofences/${id}`),

  getEvents: (id: string) => api.get(`/geofences/${id}/events`),

  checkPosition: (latitude: number, longitude: number) =>
    api.post('/geofences/check', { latitude, longitude }),

  // Export/Import
  exportGeoJson: () => api.get('/geofences/export/geojson', { responseType: 'blob' }),
  exportKml: () => api.get('/geofences/export/kml', { responseType: 'blob' }),
  importGeoJson: (geojson: object) => api.post('/geofences/import/geojson', geojson),
  importKml: (kml: string) => api.post('/geofences/import/kml', { kml }),
};

// Reports API
export const reportsApi = {
  getTrips: (params: {
    deviceIds?: string[];
    groupId?: string;
    startDate: string;
    endDate: string;
  }) => api.get('/reports/trips', {
    params: {
      ...params,
      deviceIds: params.deviceIds?.join(','),
    },
  }),

  getStops: (params: {
    deviceIds?: string[];
    groupId?: string;
    startDate: string;
    endDate: string;
  }) => api.get('/reports/stops', {
    params: {
      ...params,
      deviceIds: params.deviceIds?.join(','),
    },
  }),

  getSummary: (params: {
    deviceIds?: string[];
    groupId?: string;
    startDate: string;
    endDate: string;
  }) => api.get('/reports/summary', {
    params: {
      ...params,
      deviceIds: params.deviceIds?.join(','),
    },
  }),

  getEvents: (params: {
    deviceIds?: string[];
    groupId?: string;
    startDate: string;
    endDate: string;
    types?: string[];
  }) => api.get('/reports/events', {
    params: {
      ...params,
      deviceIds: params.deviceIds?.join(','),
      types: params.types?.join(','),
    },
  }),

  getRoute: (params: {
    deviceIds?: string[];
    groupId?: string;
    startDate: string;
    endDate: string;
  }) => api.get('/reports/route', {
    params: {
      ...params,
      deviceIds: params.deviceIds?.join(','),
    },
  }),

  // Export URLs (direct download)
  getTripsExportUrl: (params: {
    deviceIds?: string[];
    startDate: string;
    endDate: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.deviceIds?.length) searchParams.set('deviceIds', params.deviceIds.join(','));
    searchParams.set('startDate', params.startDate);
    searchParams.set('endDate', params.endDate);
    return `/api/reports/trips/export?${searchParams.toString()}`;
  },

  getStopsExportUrl: (params: {
    deviceIds?: string[];
    startDate: string;
    endDate: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.deviceIds?.length) searchParams.set('deviceIds', params.deviceIds.join(','));
    searchParams.set('startDate', params.startDate);
    searchParams.set('endDate', params.endDate);
    return `/api/reports/stops/export?${searchParams.toString()}`;
  },

  getSummaryExportUrl: (params: {
    deviceIds?: string[];
    startDate: string;
    endDate: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.deviceIds?.length) searchParams.set('deviceIds', params.deviceIds.join(','));
    searchParams.set('startDate', params.startDate);
    searchParams.set('endDate', params.endDate);
    return `/api/reports/summary/export?${searchParams.toString()}`;
  },

  getEventsExportUrl: (params: {
    deviceIds?: string[];
    startDate: string;
    endDate: string;
    types?: string[];
  }) => {
    const searchParams = new URLSearchParams();
    if (params.deviceIds?.length) searchParams.set('deviceIds', params.deviceIds.join(','));
    searchParams.set('startDate', params.startDate);
    searchParams.set('endDate', params.endDate);
    if (params.types?.length) searchParams.set('types', params.types.join(','));
    return `/api/reports/events/export?${searchParams.toString()}`;
  },

  getDriverBehavior: (params: {
    deviceIds?: string[];
    startDate: string;
    endDate: string;
  }) => api.get('/reports/driver-behavior', {
    params: {
      ...params,
      deviceIds: params.deviceIds?.join(','),
    },
  }),

  getDriverBehaviorExcelUrl: (params: {
    deviceIds?: string[];
    startDate: string;
    endDate: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.deviceIds?.length) searchParams.set('deviceIds', params.deviceIds.join(','));
    searchParams.set('startDate', params.startDate);
    searchParams.set('endDate', params.endDate);
    return `/api/reports/driver-behavior/excel?${searchParams.toString()}`;
  },

  getDriverBehaviorPdfUrl: (params: {
    deviceIds?: string[];
    startDate: string;
    endDate: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.deviceIds?.length) searchParams.set('deviceIds', params.deviceIds.join(','));
    searchParams.set('startDate', params.startDate);
    searchParams.set('endDate', params.endDate);
    return `/api/reports/driver-behavior/pdf?${searchParams.toString()}`;
  },
};

// Maintenance API
export const maintenanceApi = {
  // Types
  getTypes: () => api.get('/maintenance/types'),

  createType: (data: {
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    intervalKm?: number;
    intervalMonths?: number;
    estimatedCost?: number;
  }) => api.post('/maintenance/types', data),

  updateType: (id: string, data: Partial<{
    name: string;
    description: string;
    icon: string;
    color: string;
    intervalKm: number;
    intervalMonths: number;
    estimatedCost: number;
    isActive: boolean;
  }>) => api.put(`/maintenance/types/${id}`, data),

  deleteType: (id: string) => api.delete(`/maintenance/types/${id}`),

  initDefaultTypes: () => api.post('/maintenance/types/init'),

  // Records
  getRecords: (params?: {
    deviceId?: string;
    typeId?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }) => api.get('/maintenance/records', { params }),

  exportRecords: (params: {
    format: 'xlsx' | 'pdf';
    deviceId?: string;
    typeId?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    api.get('/maintenance/records/export', {
      params,
      responseType: 'blob',
    }),

  uploadInvoice: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/maintenance/records/upload-invoice', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteInvoice: (filename: string) =>
    api.delete(`/maintenance/records/invoice/${encodeURIComponent(filename)}`),

  createRecord: (data: {
    deviceId: string;
    typeId: string;
    performedAt: string;
    odometerKm: number;
    performedBy?: string;
    laborCost?: number;
    partsCost?: number;
    totalCost?: number;
    description?: string;
    notes?: string;
    invoiceUrl?: string;
    scheduleId?: string;
  }) => api.post('/maintenance/records', data),

  updateRecord: (id: string, data: Partial<{
    performedAt: string;
    odometerKm: number;
    performedBy: string;
    laborCost: number;
    partsCost: number;
    totalCost: number;
    description: string;
    notes: string;
    invoiceUrl: string | null;
  }>) => api.put(`/maintenance/records/${id}`, data),

  deleteRecord: (id: string) => api.delete(`/maintenance/records/${id}`),

  // Schedules
  getSchedules: (params?: {
    deviceId?: string;
    typeId?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) => api.get('/maintenance/schedules', { params }),

  createSchedule: (data: {
    deviceId: string;
    typeId: string;
    dueDate?: string;
    dueOdometerKm?: number;
    priority?: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
    estimatedCost?: number;
    notes?: string;
    notifyUserIds?: string[];
    recurrence?: 'MONTHLY' | 'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL' | 'TWO_YEARS' | 'FOUR_YEARS' | null;
  }) => api.post('/maintenance/schedules', data),

  updateSchedule: (id: string, data: Partial<{
    dueDate: string;
    dueOdometerKm: number;
    status: string;
    priority: string;
    estimatedCost: number;
    notes: string;
    notifyUserIds: string[];
    recurrence: 'MONTHLY' | 'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL' | 'TWO_YEARS' | 'FOUR_YEARS' | null;
  }>) => api.put(`/maintenance/schedules/${id}`, data),

  deleteSchedule: (id: string) => api.delete(`/maintenance/schedules/${id}`),

  // Stats
  getStats: (deviceId?: string) =>
    api.get('/maintenance/stats', { params: deviceId ? { deviceId } : undefined }),
};

// Fuel API
export type FuelProvider = 'custom' | 'dkv' | 'shell' | 'totalenergies' | 'as24' | 'eurotraffic';

export interface FuelColumnRef {
  kind: 'column' | 'constant';
  ref?: string | number;
  value?: string;
}

export interface FuelImportConfig {
  provider: FuelProvider;
  fileFormat: 'csv' | 'xlsx';
  separator: ';' | ',' | '\t' | '|';
  encoding: 'utf-8' | 'utf-8-bom' | 'iso-8859-1';
  decimalSeparator: '.' | ',';
  thousandSeparator: ' ' | '.' | ',' | '';
  dateFormat: 'yyyy-MM-dd' | 'dd/MM/yyyy' | 'dd.MM.yyyy' | 'MM/dd/yyyy';
  hasHeader: boolean;
  startRow: number;
  defaultCurrency: string;
  columnMapping: Record<string, FuelColumnRef>;
  fuelTypeMapping: Record<string, 'DIESEL' | 'PETROL' | 'ELECTRIC' | 'OTHER'>;
  matchBy: Array<'cardNumber' | 'plate'>;
}

export interface FuelProfile {
  id: string;
  name: string;
  config: FuelImportConfig;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FuelCard {
  id: string;
  cardNumber: string;
  label: string | null;
  provider: string | null;
  deviceId: string | null;
  device: { id: string; name: string; plate: string | null } | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FuelTransaction {
  id: string;
  transactionDate: string;
  deviceId: string | null;
  device: { id: string; name: string; plate: string | null } | null;
  fuelCardId: string | null;
  fuelCard: { id: string; cardNumber: string; label: string | null } | null;
  vehiclePlate: string | null;
  cardNumber: string | null;
  fuelType: string | null;
  volumeLiters: number | null;
  unitPrice: number | null;
  totalAmount: number;
  taxAmount: number | null;
  taxRate: number | null;
  currency: string;
  stationName: string | null;
  stationCountry: string | null;
  odometerKm: number | null;
  externalId: string | null;
  source: 'IMPORT' | 'OCR' | 'MANUAL';
  sourceProfileId: string | null;
  notes: string | null;
  createdAt: string;
}

export interface FuelImportSummary {
  totalRows: number;
  created: number;
  updated: number;
  skippedDuplicate: number;
  skippedInvalid: number;
  matched: number;
  unmatched: number;
  errors: Array<{ row: number; reason: string }>;
}

export const fuelApi = {
  // Profiles
  listProfiles: () => api.get<{ profiles: FuelProfile[]; activeProfileId: string | null }>('/fuel/import-profiles'),
  createProfile: (name: string, config?: FuelImportConfig) =>
    api.post<FuelProfile>('/fuel/import-profiles', config ? { name, config } : { name }),
  updateProfile: (id: string, data: { name?: string; config?: FuelImportConfig }) =>
    api.patch<FuelProfile>(`/fuel/import-profiles/${id}`, data),
  deleteProfile: (id: string) => api.delete(`/fuel/import-profiles/${id}`),
  activateProfile: (id: string) => api.post<FuelProfile>(`/fuel/import-profiles/${id}/activate`),
  getTemplate: (provider: FuelProvider) => api.get<FuelImportConfig>(`/fuel/import-templates/${provider}`),

  // Cards
  listCards: (params?: { isActive?: boolean }) =>
    api.get<{ data: FuelCard[] }>('/fuel/cards', {
      params: params?.isActive === undefined ? undefined : { isActive: String(params.isActive) },
    }),
  createCard: (data: { cardNumber: string; label?: string; provider?: string; deviceId?: string }) =>
    api.post<FuelCard>('/fuel/cards', data),
  updateCard: (id: string, data: Partial<{ cardNumber: string; label: string | null; provider: string | null; deviceId: string | null; isActive: boolean }>) =>
    api.patch<FuelCard>(`/fuel/cards/${id}`, data),
  deleteCard: (id: string) => api.delete(`/fuel/cards/${id}`),

  // Transactions
  listTransactions: (params?: {
    deviceId?: string;
    fuelCardId?: string;
    fuelType?: string;
    source?: 'IMPORT' | 'OCR' | 'MANUAL';
    unmatched?: boolean;
    dateFrom?: string;
    dateTo?: string;
    search?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const { unmatched, ...rest } = params ?? {};
    const q: Record<string, string | number | undefined> = { ...rest };
    if (unmatched) q.unmatched = 'true';
    return api.get<{ data: FuelTransaction[]; total: number; page: number; pageSize: number }>(
      '/fuel/transactions',
      { params: q },
    );
  },
  createTransaction: (data: Partial<FuelTransaction> & { transactionDate: string; totalAmount: number }) =>
    api.post<FuelTransaction>('/fuel/transactions', data),
  updateTransaction: (id: string, data: Partial<FuelTransaction>) =>
    api.patch<FuelTransaction>(`/fuel/transactions/${id}`, data),
  deleteTransaction: (id: string) => api.delete(`/fuel/transactions/${id}`),
  exportTransactions: (params?: { deviceId?: string; dateFrom?: string; dateTo?: string }) =>
    api.get('/fuel/transactions/export', { params, responseType: 'blob' }),

  // Stats
  getStats: (params?: { deviceId?: string; fuelCardId?: string; dateFrom?: string; dateTo?: string }) =>
    api.get<{
      totalAmount: number;
      totalLiters: number;
      transactionCount: number;
      unmatchedCount: number;
      avgPricePerLiter: number | null;
      byDevice: Array<{ deviceId: string; deviceName: string; plate: string | null; amount: number; liters: number }>;
    }>('/fuel/stats', { params }),

  // Timeseries (graphs for the Statistics tab)
  getTimeseries: (params?: {
    fuelCardId?: string;
    dateFrom?: string;
    dateTo?: string;
    granularity?: 'year' | 'month';
  }) =>
    api.get<{
      data: Array<{ period: string; amount: number; liters: number; transactionCount: number }>;
    }>('/fuel/stats/timeseries', { params }),

  // Import — file inspection (returns column headers + a preview of data rows)
  inspect: (
    file: File,
    opts: {
      fileFormat: 'csv' | 'xlsx';
      encoding: 'utf-8' | 'utf-8-bom' | 'iso-8859-1';
      separator: ';' | ',' | '\t' | '|';
      hasHeader: boolean;
      startRow: number;
    },
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('fileFormat', opts.fileFormat);
    fd.append('encoding', opts.encoding);
    fd.append('separator', opts.separator);
    fd.append('hasHeader', String(opts.hasHeader));
    fd.append('startRow', String(opts.startRow));
    return api.post<{ headers: string[]; preview: string[][]; totalRows: number }>(
      '/fuel/import/inspect',
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },

  // Import
  upload: (file: File, profileId: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('profileId', profileId);
    return api.post<FuelImportSummary>('/fuel/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Groups API
export const groupsApi = {
  list: (params?: {
    search?: string;
    page?: number;
    limit?: number;
  }) => api.get('/groups', { params }),

  get: (id: string) => api.get(`/groups/${id}`),

  create: (data: {
    name: string;
    description?: string;
    color?: string;
    visibleToUserIds?: string[];
  }) => api.post('/groups', data),

  update: (id: string, data: Partial<{
    name: string;
    description: string;
    color: string;
    visibleToUserIds: string[];
  }>) => api.put(`/groups/${id}`, data),

  delete: (id: string) => api.delete(`/groups/${id}`),

  getStats: () => api.get('/groups/stats'),

  getUnassigned: () => api.get('/groups/unassigned'),

  addDevices: (groupId: string, deviceIds: string[]) =>
    api.post(`/groups/${groupId}/devices`, { deviceIds }),

  removeDevices: (groupId: string, deviceIds: string[]) =>
    api.delete(`/groups/${groupId}/devices`, { data: { deviceIds } }),
};

// Scheduled Reports API
export const scheduledReportsApi = {
  list: (params?: { isActive?: boolean }) =>
    api.get('/scheduled-reports', { params }),

  get: (id: string) => api.get(`/scheduled-reports/${id}`),

  create: (data: {
    name: string;
    description?: string;
    reportType: 'SUMMARY' | 'TRIPS' | 'STOPS' | 'EVENTS';
    format?: 'PDF' | 'EXCEL';
    frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
    dayOfWeek?: number;
    dayOfMonth?: number;
    hour?: number;
    minute?: number;
    timezone?: string;
    deviceIds?: string[];
    groupId?: string;
    recipients: string[];
  }) => api.post('/scheduled-reports', data),

  update: (id: string, data: Partial<{
    name: string;
    description: string;
    reportType: 'SUMMARY' | 'TRIPS' | 'STOPS' | 'EVENTS';
    format: 'PDF' | 'EXCEL';
    frequency: 'DAILY' | 'WEEKLY' | 'MONTHLY';
    dayOfWeek: number;
    dayOfMonth: number;
    hour: number;
    minute: number;
    timezone: string;
    deviceIds: string[];
    groupId: string;
    recipients: string[];
    isActive: boolean;
  }>) => api.put(`/scheduled-reports/${id}`, data),

  delete: (id: string) => api.delete(`/scheduled-reports/${id}`),

  run: (id: string) => api.post(`/scheduled-reports/${id}/run`),

  getEmailStatus: () => api.get('/scheduled-reports/email/status'),
};

// Drivers API
export const driversApi = {
  list: (params?: {
    search?: string;
    isActive?: boolean;
    page?: number;
    limit?: number;
  }) => api.get('/drivers', { params }),

  get: (id: string) => api.get(`/drivers/${id}`),

  create: (data: {
    name?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
    password?: string;
    licenseNumber?: string;
    licenseExpiry?: string;
    iButtonId?: string;
    rfidId?: string;
    [key: string]: unknown;
  }) => api.post('/drivers', data),

  update: (id: string, data: Partial<{
    name: string;
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    licenseNumber: string;
    licenseExpiry: string;
    iButtonId: string;
    rfidId: string;
    isActive: boolean;
  }> & Record<string, unknown>) => api.put(`/drivers/${id}`, data),

  delete: (id: string) => api.delete(`/drivers/${id}`),

  setPassword: (id: string, password: string, email?: string) =>
    api.post(`/drivers/${id}/set-password`, { password, ...(email ? { email } : {}) }),

  getStats: () => api.get('/drivers/stats'),

  findByIButton: (iButtonId: string) => api.get(`/drivers/identify/ibutton/${iButtonId}`),

  findByRfid: (rfidId: string) => api.get(`/drivers/identify/rfid/${rfidId}`),

  // Scoring
  getScores: (params?: {
    period?: string;
    date?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    page?: number;
    limit?: number;
  }) => api.get('/drivers/scores', { params }),

  getScore: (id: string, params?: { date?: string; period?: string }) =>
    api.get(`/drivers/${id}/score`, { params }),

  getScoreHistory: (id: string, params?: { period?: string; days?: number }) =>
    api.get(`/drivers/${id}/score/history`, { params }),

  computeScores: () => api.post('/drivers/scores/compute'),

  setMissionsEnabled: (id: string, enabled: boolean) =>
    api.patch<{ id: string; missionsEnabled: boolean; seatsUsed: number; seatsSold: number }>(
      `/drivers/${id}/missions-enabled`,
      { enabled },
    ),
};

// Billing API
export const billingApi = {
  getPlans: () => api.get('/billing/plans'),

  getSubscription: () => api.get('/billing/subscription'),

  getSubscriptions: () => api.get('/billing/subscriptions'),

  getInvoices: (params?: { page?: number; limit?: number }) =>
    api.get('/billing/invoices', { params }),

  createCheckout: (priceId: string, quantity?: number, returnPath?: string) =>
    api.post('/billing/checkout', { priceId, quantity: quantity || 1, returnPath }),

  createPortal: () => api.post('/billing/portal'),

  cancelSubscription: (immediately?: boolean) =>
    api.post('/billing/cancel', { immediately }),

  payInvoice: (invoiceId: string) =>
    api.post<{ url: string }>(`/billing/invoices/${invoiceId}/pay`),

  getUsage: () => api.get('/billing/usage'),

  reactivateSubscription: (subscriptionId: string) =>
    api.post('/billing/reactivate', { subscriptionId }),
};

// Super Admin API (requires SUPER_ADMIN role)
export const adminApi = {
  // Dashboard
  getStats: () => api.get('/admin/stats'),

  // Tenants
  getTenants: (params?: {
    search?: string;
    plan?: string;
    status?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get('/admin/tenants', { params }),

  getTenant: (id: string) => api.get(`/admin/tenants/${id}`),

  createTenant: (data: {
    name: string;
    slug: string;
    adminEmail: string;
    adminPassword: string;
    adminFirstName: string;
    adminLastName: string;
    plan?: string;
    maxDevices?: number;
    maxUsers?: number;
  }) => api.post('/admin/tenants', data),

  updateTenant: (id: string, data: Partial<{
    name: string;
    isActive: boolean;
    plan: string;
    maxDevices: number;
    maxUsers: number;
    maxGeofences: number;
    retentionDays: number;
    // Company info
    companyName: string | null;
    address: string | null;
    addressLine2: string | null;
    city: string | null;
    postalCode: string | null;
    country: string | null;
    phone: string | null;
    email: string | null;
    vatNumber: string | null;
    siret: string | null;
    website: string | null;
    // Billing
    billingEmail: string | null;
    billingAddress: string | null;
    vatRate: number;
    // Duo Security
    duoClientId: string | null;
    duoClientSecret: string | null;
    duoApiHostname: string | null;
    duoEnabled: boolean;
    // API Keys
    apiKeysEnabled: boolean;
  }>) => api.put(`/admin/tenants/${id}`, data),

  duoHealthCheck: (tenantId: string, redirectUri: string) =>
    api.post<{ success: boolean; message: string }>(`/admin/tenants/${tenantId}/duo-health-check`, { redirectUri }),

  deleteTenant: (id: string) => api.delete(`/admin/tenants/${id}`),

  bulkToggleActiveTenants: (ids: string[], isActive: boolean) =>
    api.post<{ updated: number; isActive: boolean }>('/admin/tenants/bulk-toggle-active', { ids, isActive }),

  bulkDeleteTenants: (ids: string[]) =>
    api.post<{ deleted: number }>('/admin/tenants/bulk-delete', { ids }),

  // Tenant users
  getTenantUsers: (tenantId: string) => api.get(`/admin/tenants/${tenantId}/users`),

  // Reset tenant admin password
  resetTenantPassword: (tenantId: string, password: string) =>
    api.post(`/admin/tenants/${tenantId}/reset-password`, { password }),

  // Tenant add-on modules
  getModuleCatalog: () =>
    api.get<
      Array<{
        key: string;
        label: string;
        description: string;
        defaultPriceCents: number;
        icon: string;
        priority: 1 | 2 | 3;
        dependencies?: string[];
      }>
    >('/admin/modules/catalog'),
  getTenantModules: (tenantId: string) =>
    api.get<{
      modules: Array<{
        definition: {
          key: string;
          label: string;
          description: string;
          defaultPriceCents: number;
          icon: string;
          priority: 1 | 2 | 3;
          dependencies?: string[];
        };
        state: {
          enabled: boolean;
          pricePerVehicleCents: number;
          seatsSold: number | null;
          pricePerSeatCents: number | null;
          missionSeatsUsed: number | null;
          trialEndsAt: string | null;
          activatedAt: string | null;
          deactivatedAt: string | null;
          stripeSubscriptionItemId: string | null;
          stripePriceId: string | null;
          createdAt: string | null;
          updatedAt: string | null;
        };
      }>;
      activeVehicles: number;
      evVehicles: number;
    }>(`/admin/tenants/${tenantId}/modules`),
  activateTenantModule: (
    tenantId: string,
    moduleKey: string,
    data: { trial?: boolean; priceCents?: number; reason?: string } = {},
  ) => api.post(`/admin/tenants/${tenantId}/modules/${moduleKey}/activate`, data),
  deactivateTenantModule: (tenantId: string, moduleKey: string, data: { reason?: string } = {}) =>
    api.post(`/admin/tenants/${tenantId}/modules/${moduleKey}/deactivate`, data),
  updateTenantModulePrice: (
    tenantId: string,
    moduleKey: string,
    priceCents: number,
    reason?: string,
  ) => api.patch(`/admin/tenants/${tenantId}/modules/${moduleKey}`, { priceCents, reason }),
  updateTenantModuleSeats: (
    tenantId: string,
    moduleKey: string,
    seatsSold: number,
    pricePerSeatCents: number,
    reason?: string,
  ) =>
    api.patch(`/admin/tenants/${tenantId}/modules/${moduleKey}/seats`, {
      seatsSold,
      pricePerSeatCents,
      reason,
    }),
  getTenantModuleEvents: (
    tenantId: string,
    params?: { moduleKey?: string; limit?: number },
  ) =>
    api.get<
      Array<{
        id: string;
        moduleKey: string;
        action: string;
        previousState: unknown;
        newState: unknown;
        reason: string | null;
        triggeredBy: {
          id: string;
          firstName: string;
          lastName: string;
          email: string;
        } | null;
        createdAt: string;
      }>
    >(`/admin/tenants/${tenantId}/modules/events`, { params }),

  // EV models catalog (SUPER_ADMIN)
  listEvModels: (params?: { q?: string; activeOnly?: boolean }) =>
    api.get<EvModel[]>('/admin/ev/models', { params }),
  createEvModel: (data: EvModelInput) =>
    api.post<EvModel>('/admin/ev/models', data),
  updateEvModel: (id: string, data: Partial<EvModelInput>) =>
    api.patch<EvModel>(`/admin/ev/models/${id}`, data),
  deleteEvModel: (id: string) =>
    api.delete<{ success: boolean }>(`/admin/ev/models/${id}`),

  // Plans
  getPlans: () => api.get('/admin/plans'),

  createPlan: (data: {
    name: string;
    displayName: string;
    description?: string;
    priceOneShot?: number;
    priceYearly?: number;
    allowOneShot?: boolean;
    allowYearly?: boolean;
    usersPerTracker?: number;
    geofencesPerTracker?: number;
    retentionDays?: number;
    features?: string[];
    sortOrder?: number;
    highlightLabel?: string;
  }) => api.post('/admin/plans', data),

  updatePlan: (id: string, data: Partial<{
    displayName: string;
    description: string;
    priceOneShot: number;
    priceYearly: number;
    allowOneShot: boolean;
    allowYearly: boolean;
    usersPerTracker: number;
    geofencesPerTracker: number;
    retentionDays: number;
    features: string[];
    sortOrder: number;
    highlightLabel: string;
  }>) => api.put(`/admin/plans/${id}`, data),

  deletePlan: (id: string) => api.delete(`/admin/plans/${id}`),

  getPlanSubscriptions: (planId: string) => api.get(`/admin/plans/${planId}/subscriptions`),

  migratePlanSubscriptions: (planId: string, targetPlanId: string) =>
    api.post(`/admin/plans/${planId}/migrate`, { targetPlanId }),

  // Devices (all tenants)
  getDevices: (params?: {
    tenantId?: string;
    status?: string;
    search?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get('/admin/devices', { params }),

  getDevice: (id: string) => api.get(`/admin/devices/${id}`),

  createDevice: (data: {
    tenantId: string;
    imei?: string;
    oemProvider?: string | null;
    name: string;
    plate?: string | null;
    brand?: string | null;
    model?: string | null;
    year?: number | null;
    vin?: string | null;
    trackerModel?: string | null;
    simNumber?: string | null;
    simIccid?: string | null;
    simType?: string | null;
    category?: string | null;
    icon?: string | null;
    markerColor?: string | null;
    speedLimit?: number | null;
    fuelCapacity?: number | null;
    groupId?: string | null;
    isActive?: boolean;
  }) => api.post('/admin/devices', data),

  updateDevice: (id: string, data: Partial<{
    name: string;
    plate: string | null;
    brand: string | null;
    model: string | null;
    year: number | null;
    vin: string | null;
    trackerModel: string | null;
    simNumber: string | null;
    simIccid: string | null;
    simType: string | null;
    category: string | null;
    icon: string | null;
    markerColor: string | null;
    speedLimit: number | null;
    fuelCapacity: number | null;
    groupId: string | null;
    tenantId: string;
    isActive: boolean;
  }>) => api.put(`/admin/devices/${id}`, data),

  deleteDevice: (id: string) => api.delete(`/admin/devices/${id}`),

  transferDevice: (id: string, targetTenantId: string) =>
    api.post(`/admin/devices/${id}/transfer`, { targetTenantId }),

  importDevices: (tenantId: string, devices: Array<{
    imei: string;
    name: string;
    plate?: string;
    brand?: string;
    model?: string;
    year?: number;
    color?: string;
    category?: string;
    speedLimit?: number;
    fuelCapacity?: number;
    simNumber?: string;
  }>) => api.post<{
    success: boolean;
    imported: number;
    skipped: number;
    errors: Array<{ imei: string; error: string }>;
    total: number;
  }>('/admin/devices/import', { tenantId, devices }),

  getImportTemplate: () => `${api.defaults.baseURL}/devices/import/template`,

  // Subscriptions
  getSubscriptions: (params?: {
    status?: string;
    search?: string;
    tenantId?: string;
    planId?: string;
    page?: number;
    limit?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get('/admin/subscriptions', { params }),

  getSubscription: (id: string) => api.get(`/admin/subscriptions/${id}`),

  createSubscription: (data: {
    tenantId: string;
    planId: string;
    name?: string | null;
    quantity?: number;
    pricingType?: string;
    interval?: string;
    status?: string;
    startDate?: string | null;
    trialEndsAt?: string | null;
    autoRenewal?: boolean;
    alertDays?: number[];
    alertsEnabled?: boolean;
    expiresAt?: string | null;
    notes?: string | null;
    deviceIds?: string[];
  }) => api.post('/admin/subscriptions', data),

  updateSubscription: (id: string, data: Partial<{
    planId: string;
    name: string | null;
    quantity: number;
    pricingType: string;
    interval: string;
    status: string;
    startDate: string | null;
    trialEndsAt: string | null;
    autoRenewal: boolean;
    alertDays: number[];
    alertsEnabled: boolean;
    expiresAt: string | null;
    notes: string | null;
    deviceIds: string[];
  }>) => api.put(`/admin/subscriptions/${id}`, data),

  syncSubscriptionDevices: () => api.post('/admin/subscriptions/sync-devices'),

  bulkToggleActiveSubscriptions: (ids: string[], isActive: boolean) =>
    api.post<{ updated: number; isActive: boolean }>('/admin/subscriptions/bulk-toggle-active', { ids, isActive }),

  bulkToggleRenewalSubscriptions: (ids: string[], autoRenewal: boolean) =>
    api.post<{ updated: number; autoRenewal: boolean }>('/admin/subscriptions/bulk-toggle-renewal', { ids, autoRenewal }),

  bulkDeleteSubscriptions: (ids: string[]) =>
    api.post<{ deleted: number }>('/admin/subscriptions/bulk-delete', { ids }),

  renewSubscription: (id: string) => api.post(`/admin/subscriptions/${id}/renew`),

  createSubscriptionCheckout: (id: string) =>
    api.post<{ url: string }>(`/admin/subscriptions/${id}/checkout`),

  deleteSubscription: (id: string, immediate: boolean = false) =>
    api.delete(`/admin/subscriptions/${id}`, { params: { immediate } }),

  // Invoices
  getInvoices: (params?: { status?: string; subscriptionId?: string; page?: number; limit?: number }) =>
    api.get('/admin/invoices', { params }),

  getPendingInvoices: () =>
    api.get('/admin/invoices/pending'),

  getExpiringSoonInvoices: (days?: number) =>
    api.get('/admin/invoices/expiring-soon', { params: { days } }),

  generateInvoice: (subscriptionId: string) =>
    api.post('/admin/invoices/generate', { subscriptionId }),

  downloadInvoice: (id: string) =>
    api.get(`/admin/invoices/${id}/download`, { responseType: 'blob' }),

  sendInvoice: (id: string) =>
    api.post(`/admin/invoices/${id}/send`),

  convertInvoice: (id: string) =>
    api.post(`/admin/invoices/${id}/convert`),

  refuseInvoice: (id: string) =>
    api.post(`/admin/invoices/${id}/refuse`),

  markInvoicePaid: (id: string) =>
    api.post(`/admin/invoices/${id}/mark-paid`),

  // Audit Logs
  getAuditLogs: (params?: {
    tenantId?: string;
    userId?: string;
    action?: string;
    entityType?: string;
    startDate?: string;
    endDate?: string;
    page?: number;
    limit?: number;
  }) => api.get('/admin/audit-logs', { params }),

  // System Health
  getHealth: () => api.get('/admin/health'),

  // Email Settings
  getEmailSettings: () =>
    api.get<{
      settings: {
        host: string;
        port: number;
        secure: boolean;
        user: string;
        from: string;
        enabled: boolean;
        hasPassword: boolean;
      } | null;
      usingEnvVars: boolean;
      envConfigured: boolean;
    }>('/admin/email/settings'),

  updateEmailSettings: (data: {
    host: string;
    port: number;
    secure?: boolean;
    user: string;
    password?: string;
    from: string;
    enabled?: boolean;
  }) => api.put('/admin/email/settings', data),

  testEmailConnection: (data: {
    host: string;
    port: number;
    secure?: boolean;
    user: string;
    password?: string;
    from: string;
  }) => api.post<{ success: boolean; error?: string }>('/admin/email/test-connection', data),

  getEmailStatus: () =>
    api.get<{ configured: boolean; connected?: boolean; message: string }>('/admin/email/status'),

  sendTestEmail: (data: { to: string; subject?: string; message?: string }) =>
    api.post('/admin/email/test', data),

  // Twilio SMS Settings
  getTwilioSettings: () =>
    api.get<{
      settings: {
        accountSid: string;
        fromNumber: string;
        senderId: string;
        enabled: boolean;
        hasAuthToken: boolean;
      } | null;
      configured: boolean;
    }>('/admin/twilio/settings'),

  updateTwilioSettings: (data: {
    accountSid: string;
    authToken?: string;
    fromNumber: string;
    senderId?: string;
    enabled?: boolean;
  }) => api.put('/admin/twilio/settings', data),

  testTwilioSms: (data: { to: string; message?: string }) =>
    api.post<{ success: boolean; message: string }>('/admin/twilio/test', data),

  // Truphone SMS Settings
  getTruphoneSettings: () =>
    api.get<{
      settings: {
        identifier: string;
        enabled: boolean;
        hasToken: boolean;
        hasPassword: boolean;
      } | null;
      configured: boolean;
    }>('/admin/truphone/settings'),

  updateTruphoneSettings: (data: {
    token?: string;
    identifier: string;
    password?: string;
    enabled?: boolean;
  }) => api.put('/admin/truphone/settings', data),

  testTruphoneConnection: () =>
    api.post<{ success: boolean; message: string }>('/admin/truphone/test'),

  sendTruphoneSms: (data: { deviceId?: string; iccid: string; command: string }) =>
    api.post<{ success: boolean; message: string }>('/admin/truphone/send-sms', data),

  getSmsLogs: (params?: { page?: number; limit?: number }) =>
    api.get<{
      data: Array<{
        id: string;
        deviceId: string | null;
        iccid: string;
        message: string;
        status: string;
        error: string | null;
        sentBy: string | null;
        createdAt: string;
        device: { id: string; name: string; imei: string } | null;
      }>;
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    }>('/admin/truphone/sms-logs', { params }),

  getTruphoneSmsHistory: () =>
    api.get<{
      data: Array<{
        id: string;
        truphoneId: string;
        date: string;
        content: string;
        iccid: string;
        deliveryStatus: string;
        type: 'command' | 'response';
        device: { id: string; name: string; imei: string; simIccid: string | null } | null;
      }>;
      total: number;
    }>('/admin/truphone/sms-history'),

  // FOTA Web Settings
  getFotaWebSettings: () =>
    api.get<{
      settings: {
        apiUrl: string;
        enabled: boolean;
        hasApiToken: boolean;
      } | null;
      configured: boolean;
    }>('/admin/fotaweb/settings'),

  updateFotaWebSettings: (data: {
    apiToken?: string;
    apiUrl?: string;
    enabled?: boolean;
  }) => api.put('/admin/fotaweb/settings', data),

  testFotaWebConnection: () =>
    api.post<{ success: boolean; message: string }>('/admin/fotaweb/test'),

  getFotaWebDevices: (imeis: string[]) =>
    api.get('/admin/fotaweb/devices', { params: { imeis: imeis.join(',') } }),

  pushFotaWebConfig: (data: { imeis: string[]; cfgBase64: string; filename: string }) =>
    api.post<{
      success: boolean;
      fileId: number;
      taskCount: number;
      failedCount: number;
      tasks: Array<{ imei: string; taskId?: number; error?: string }>;
    }>('/admin/fotaweb/push-config', data),

  getFotaWebTasks: (ids: number[]) =>
    api.get('/admin/fotaweb/tasks', { params: { ids: ids.join(',') } }),

  retryFotaWebBatch: (batchId: number) =>
    api.post('/admin/fotaweb/retry/' + batchId),

  cancelFotaWebTasks: (taskIds: number[]) =>
    api.post('/admin/fotaweb/cancel-tasks', { taskIds }),

  // Firebase Push Settings
  getFirebaseSettings: () =>
    api.get<{
      settings: {
        projectId: string;
        clientEmail: string;
        apiKey: string;
        messagingSenderId: string;
        appId: string;
        vapidKey: string;
        enabled: boolean;
        hasPrivateKey: boolean;
      } | null;
      configured: boolean;
    }>('/admin/firebase/settings'),

  updateFirebaseSettings: (data: {
    projectId: string;
    privateKey?: string;
    clientEmail: string;
    apiKey?: string;
    messagingSenderId?: string;
    appId?: string;
    vapidKey?: string;
    enabled?: boolean;
  }) => api.put('/admin/firebase/settings', data),

  testFirebasePush: (data: { token: string; title?: string; body?: string }) =>
    api.post<{ success: boolean; message: string }>('/admin/firebase/test', data),

  // WhatsApp Settings
  getWhatsAppSettings: () =>
    api.get<{
      settings: {
        phoneNumberId: string;
        businessAccountId: string;
        enabled: boolean;
        hasAccessToken: boolean;
      } | null;
      configured: boolean;
    }>('/admin/whatsapp/settings'),

  updateWhatsAppSettings: (data: {
    phoneNumberId: string;
    accessToken?: string;
    businessAccountId: string;
    enabled?: boolean;
  }) => api.put('/admin/whatsapp/settings', data),

  testWhatsApp: (data: { to: string }) =>
    api.post<{ success: boolean; message: string }>('/admin/whatsapp/test', data),

  verifyWhatsApp: () =>
    api.post<{ connected: boolean }>('/admin/whatsapp/verify'),

  // Stripe Settings
  getStripeSettings: () =>
    api.get<{
      settings: {
        hasSecretKey: boolean;
        hasWebhookSecret: boolean;
        publishableKey: string;
        enabled: boolean;
      } | null;
      usingEnvVars: boolean;
      envConfigured: boolean;
    }>('/admin/stripe/settings'),

  updateStripeSettings: (data: {
    secretKey?: string;
    webhookSecret?: string;
    publishableKey?: string;
    enabled?: boolean;
  }) => api.put('/admin/stripe/settings', data),

  testStripeConnection: (data: { secretKey?: string }) =>
    api.post<{ success: boolean; message?: string; error?: string }>('/admin/stripe/test', data),

  // High Mobility (OEM Connect) platform credentials
  getOemSettings: () =>
    api.get<{
      settings: {
        clientId: string;
        environment: 'sandbox' | 'production';
        enabled: boolean;
        hasClientSecret: boolean;
      } | null;
      configured: boolean;
    }>('/admin/oem/settings'),

  updateOemSettings: (data: {
    clientId: string;
    clientSecret?: string;
    environment?: 'sandbox' | 'production';
    enabled?: boolean;
  }) => api.put('/admin/oem/settings', data),

  checkOemEligibility: (vin: string, brand?: string) =>
    api.post<{
      vin: string;
      eligible: boolean;
      brand?: string;
      connectivityStatus?: string;
      dataDelivery?: string[];
      primaryUserAssigned?: boolean;
    }>('/admin/oem/eligibility', { vin, brand }),

  // Subscription Alerts
  getExpiringSubscriptions: (days?: number) =>
    api.get('/admin/subscriptions/expiring', { params: { days } }),

  triggerSubscriptionAlertsCheck: () =>
    api.post('/admin/subscriptions/check-alerts'),

  // Appearance Settings
  getAppearanceSettings: () =>
    api.get('/admin/settings/appearance').then((res) => res.data),

  updateAppearanceSettings: (data: {
    companyName: string;
    companyTagline?: string;
    logo?: string;
    logoForInvoices?: string;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    legalName?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    phone?: string;
    email?: string;
    website?: string;
    vatNumber?: string;
    siret?: string;
    supportEmail?: string;
    showRegistration?: boolean;
    iban?: string;
    bic?: string;
    rcs?: string;
    capitalSocial?: string;
    formeJuridique?: string;
  }) => api.put('/admin/settings/appearance', data),

  uploadLogo: (logo: string, type: 'main' | 'invoice') =>
    api.post('/admin/settings/logo', { logo, type }),

  // Invoice CGV
  getInvoiceCgv: () =>
    api.get('/admin/settings/invoice-cgv').then((res) => res.data as { text: string }),

  updateInvoiceCgv: (text: string) =>
    api.put('/admin/settings/invoice-cgv', { text }),

  // Legal texts (CGU + Privacy)
  getLegalTexts: () =>
    api.get('/admin/settings/legal-texts').then((res) => res.data as { cguText: string; privacyText: string }),

  updateLegalTexts: (cguText: string, privacyText: string) =>
    api.put('/admin/settings/legal-texts', { cguText, privacyText }),

  // Tracker Parameters
  getTrackerParameters: (params?: {
    category?: string;
    isActive?: boolean;
    isPremium?: boolean;
    search?: string;
  }) => api.get('/admin/tracker-parameters', { params }),

  getTrackerParameter: (id: string) => api.get(`/admin/tracker-parameters/${id}`),

  createTrackerParameter: (data: {
    code: string;
    name: string;
    description?: string;
    category: string;
    subcategory?: string;
    dataType?: string;
    unit?: string;
    avlId?: number | null;
    icon?: string;
    color?: string;
    sortOrder?: number;
    isActive?: boolean;
    isDefault?: boolean;
    isPremium?: boolean;
    requiresObd?: boolean;
    requiresBluetooth?: boolean;
  }) => api.post('/admin/tracker-parameters', data),

  updateTrackerParameter: (id: string, data: Partial<{
    code: string;
    name: string;
    description: string;
    category: string;
    subcategory: string;
    dataType: string;
    unit: string;
    avlId: number | null;
    icon: string;
    color: string;
    sortOrder: number;
    isActive: boolean;
    isDefault: boolean;
    isPremium: boolean;
    requiresObd: boolean;
    requiresBluetooth: boolean;
  }>) => api.put(`/admin/tracker-parameters/${id}`, data),

  deleteTrackerParameter: (id: string) => api.delete(`/admin/tracker-parameters/${id}`),

  getTrackerParameterCategories: () => api.get('/admin/tracker-parameters-categories'),

  // Plan Parameters
  getPlanParameters: (planId: string) => api.get(`/admin/plans/${planId}/parameters`),

  updatePlanParameters: (planId: string, parameterIds: string[]) =>
    api.put(`/admin/plans/${planId}/parameters`, { parameterIds }),

  togglePlanParameter: (planId: string, parameterId: string, enabled: boolean) =>
    api.post(`/admin/plans/${planId}/parameters/toggle`, { parameterId, enabled }),

  applyDefaultParameters: (planId: string) =>
    api.post(`/admin/plans/${planId}/parameters/apply-defaults`),

  // Push Notifications
  getFirebasePublicConfig: () =>
    api.get<{
      apiKey: string;
      authDomain: string;
      projectId: string;
      storageBucket: string;
      messagingSenderId: string;
      appId: string;
      vapidKey: string;
    }>('/public/firebase-config'),

  registerPushToken: (data: { token: string }) =>
    api.post('/push/register', data),

  unregisterPushToken: (data: { token: string }) =>
    api.post('/push/unregister', data),

  // Notification Templates
  getNotificationTemplates: () =>
    api.get<{
      templates: {
        email: Record<string, { subject: string; body: string }>;
        sms: Record<string, string>;
        push: Record<string, { title: string; body: string }>;
      };
      alertTypes: string[];
      alertTypeLabels: Record<string, string>;
      variables: Array<{ variable: string; description: string; example: string }>;
    }>('/admin/notification-templates'),

  updateNotificationTemplates: (data: {
    email?: Record<string, { subject: string; body: string }>;
    sms?: Record<string, string>;
    push?: Record<string, { title: string; body: string }>;
  }) => api.put<{ success: boolean; message: string }>('/admin/notification-templates', data),

  resetNotificationTemplates: () =>
    api.post<{
      success: boolean;
      message: string;
      templates: {
        email: Record<string, { subject: string; body: string }>;
        sms: Record<string, string>;
        push: Record<string, { title: string; body: string }>;
      };
    }>('/admin/notification-templates/reset'),

  previewNotificationTemplate: (data: {
    channel: 'email' | 'sms' | 'push';
    alertType: string;
    template?: { subject?: string; body?: string; title?: string } | string;
  }) => api.post<{ preview: any }>('/admin/notification-templates/preview', data),

  // Client Email Templates
  getClientEmailTemplates: () =>
    api.get<{
      style: {
        logoUrl: string;
        logoWidth: number;
        logoHeight: number;
        primaryColor: string;
        secondaryColor: string;
        backgroundColor: string;
        textColor: string;
        headerBackgroundColor: string;
        buttonColor: string;
        buttonTextColor: string;
        fontFamily: string;
        fontSize: number;
        headingFontSize: number;
        lineHeight: number;
        maxWidth: number;
        padding: number;
        borderRadius: number;
      };
      templates: Record<string, {
        id: string;
        name: string;
        description: string;
        subject: string;
        htmlBody: string;
        variables: string[];
        enabled: boolean;
      }>;
      templateTypes: string[];
      templateTypeLabels: Record<string, string>;
      templateVariables: Record<string, Array<{ variable: string; description: string }>>;
      defaultStyle: any;
    }>('/admin/client-email-templates'),

  updateClientEmailStyle: (style: Partial<{
    logoUrl: string;
    logoWidth: number;
    logoHeight: number;
    primaryColor: string;
    secondaryColor: string;
    backgroundColor: string;
    textColor: string;
    headerBackgroundColor: string;
    buttonColor: string;
    buttonTextColor: string;
    fontFamily: string;
    fontSize: number;
    headingFontSize: number;
    lineHeight: number;
    maxWidth: number;
    padding: number;
    borderRadius: number;
  }>) => api.put<{ success: boolean; message: string }>('/admin/client-email-templates/style', style),

  updateClientEmailTemplate: (templateId: string, template: Partial<{
    subject: string;
    htmlBody: string;
    enabled: boolean;
  }>) => api.put<{ success: boolean; message: string }>(`/admin/client-email-templates/${templateId}`, template),

  resetClientEmailTemplates: () =>
    api.post<{
      success: boolean;
      message: string;
      style: any;
      templates: any;
    }>('/admin/client-email-templates/reset'),

  previewClientEmailTemplate: (templateId: string) =>
    api.post<{ preview: { subject: string; html: string } }>(`/admin/client-email-templates/preview/${templateId}`),

  sendTestClientEmail: (templateId: string, email: string) =>
    api.post<{ success: boolean; message: string }>('/admin/client-email-templates/send-test', { templateId, email }),

  // System Health
  getSystemHealth: () => api.get('/admin/system/health'),

  // Health Check (daily diagnostic report)
  getHealthCheck: () => api.get('/admin/system/health-check'),

  // System Modules
  getSystemModules: () =>
    api.get('/admin/system/modules'),

  updateSystemModules: (data: { packages: Array<{ name: string; scope: 'backend' | 'frontend' }> }) =>
    api.post('/admin/system/update', data),

  runLoadTest: (data: { type: 'api' | 'websocket' | 'mixed' | 'tracker'; concurrency: number; durationSeconds: number }) =>
    api.post('/admin/system/load-test', data, { timeout: 35000 }),

  getSystemMetrics: () =>
    api.get<{
      timestamp: string;
      http: { totalRequests: number; errorRequests: number; errorRate: number; avgResponseTimeMs: number; requestCount: number };
      tcp: {
        activeConnections: Array<{ labels: Record<string, string>; value: number }>;
        totalConnections: Array<{ labels: Record<string, string>; value: number }>;
        messagesReceived: Array<{ labels: Record<string, string>; value: number }>;
        errors: Array<{ labels: Record<string, string>; value: number }>;
      };
      websocket: { activeConnections: number };
      database: { totalQueries: number; avgLatencyMs: number };
      redis: { totalOperations: number; avgLatencyMs: number };
      business: { positionsProcessed: number; devicesOnline: number };
      process: { residentMemoryMB: number; heapUsedMB: number; heapTotalMB: number; cpuUserSeconds: number; cpuSystemSeconds: number; eventLoopLagMs: number };
    }>('/admin/system/metrics'),

  // Truphone Import
  truphoneImportSend: () =>
    api.post<{
      sent: number;
      skipped: number;
      totalActive: number;
      sims: Array<{ iccid: string; msisdn: string; label: string; tag: string }>;
    }>('/admin/truphone/import-send'),

  truphoneImportCollect: () =>
    api.post<{
      newDevices: Array<{ iccid: string; msisdn: string; imei: string; trackerModel: string; simLabel: string; tag: string }>;
      existingDevices: Array<{ iccid: string; imei: string; deviceName: string; tenantName: string }>;
      noResponse: Array<{ iccid: string; msisdn: string; simLabel: string }>;
      totalActive: number;
    }>('/admin/truphone/import-collect'),

  truphoneImportCreate: (data: {
    tenantId: string;
    devices: Array<{ iccid: string; imei: string; trackerModel: string; name: string; msisdn: string; tag?: string }>;
  }) => api.post<{
    created: number;
    skipped: number;
    errors: Array<{ imei: string; error: string }>;
  }>('/admin/truphone/import-create', data),

  // Bulk SMS
  bulkSms: (deviceIds: string[], command: string) =>
    api.post<{
      success: boolean;
      sent: number;
      skipped: number;
      devices: Array<{ id: string; name: string; imei: string }>;
    }>('/admin/truphone/bulk-sms', { deviceIds, command }),

  // Bulk Delete
  bulkDeleteDevices: (ids: string[]) =>
    api.post<{ deleted: number; skipped: number }>('/admin/devices/bulk-delete', { ids }),

  // Bulk Transfer
  bulkTransferDevices: (ids: string[], targetTenantId: string) =>
    api.post<{ transferred: number; skipped: number; targetTenant: string }>('/admin/devices/bulk-transfer', { ids, targetTenantId }),

  // Bulk Toggle Active
  bulkToggleActiveDevices: (ids: string[], isActive: boolean) =>
    api.post<{ updated: number; skipped: number; isActive: boolean }>('/admin/devices/bulk-toggle-active', { ids, isActive }),

  // Beacons
  getBeacons: (params?: { tenantId?: string; search?: string; page?: number; limit?: number }) =>
    api.get('/admin/beacons', { params }),

  createBeacon: (data: { tenantId: string; macAddress: string; name?: string; type?: string; assignedDeviceId?: string }) =>
    api.post('/admin/beacons', data),

  deleteBeacon: (id: string) => api.delete(`/admin/beacons/${id}`),
};

// Admin Settings API (roles + admin users)
export const adminSettingsApi = {
  // Permissions
  getPermissions: () => api.get('/admin/settings/permissions'),

  // Roles
  getRoles: () => api.get('/admin/settings/roles'),

  createRole: (data: { name: string; description?: string | null; permissions: string[] }) =>
    api.post('/admin/settings/roles', data),

  updateRole: (id: string, data: { name: string; description?: string | null; permissions: string[] }) =>
    api.put(`/admin/settings/roles/${id}`, data),

  deleteRole: (id: string) => api.delete(`/admin/settings/roles/${id}`),

  // Admin Users
  getUsers: () => api.get('/admin/settings/users'),

  createUser: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    adminRoleId?: string | null;
  }) => api.post('/admin/settings/users', data),

  updateUser: (id: string, data: {
    firstName?: string;
    lastName?: string;
    isActive?: boolean;
    adminRoleId?: string | null;
    notifyHealthCheck?: boolean;
  }) => api.put(`/admin/settings/users/${id}`, data),

  deleteUser: (id: string) => api.delete(`/admin/settings/users/${id}`),

  resetPassword: (id: string, password: string) =>
    api.post(`/admin/settings/users/${id}/reset-password`, { password }),
};

// Tenant Settings API (roles & permissions)
export const tenantSettingsApi = {
  getPermissions: () => api.get('/tenant/settings/permissions'),

  getRoles: () => api.get('/tenant/settings/roles'),

  updateRole: (role: string, data: { permissions: string[]; mobileNavTabs?: string[] }) =>
    api.put(`/tenant/settings/roles/${role}`, data),

  resetRole: (role: string) =>
    api.post(`/tenant/settings/roles/${role}/reset`),

  // Privacy defaults inherited by every device without its own override.
  getPrivacyDefaults: () => api.get('/tenant/settings/privacy'),

  updatePrivacyDefaults: (data: {
    enabled: boolean;
    /** WORKING hours keyed by ISO weekday; privacy is their complement. */
    workSchedule: Record<string, { allDay: true } | { start: string; end: string } | null> | null;
  }) => api.put('/tenant/settings/privacy', data),
};

// Public API (no auth required)
// Driver-declared privacy periods (leave, weekly rest, personal use), on top
// of the recurring window an admin configures per device.
export type PrivacyReason = 'LEAVE' | 'WEEKLY_REST' | 'PERSONAL_USE' | 'OTHER';

export interface PrivacyPeriod {
  id: string;
  deviceId: string;
  startsAt: string;
  endsAt: string;
  reason: PrivacyReason;
  createdAt: string;
}

export interface MyPrivacyDevice {
  id: string;
  name: string;
  plate: string | null;
  activeNow: boolean;
  work: {
    timezone: string;
    enabled: boolean;
    workSchedule: Record<string, { allDay: true } | { start: string; end: string } | null> | null;
  } | null;
  periods: PrivacyPeriod[];
}

export const privacyApi = {
  /** The caller's own vehicles, their effective window, and their periods. */
  getMine: () =>
    api.get<{ devices: MyPrivacyDevice[] }>('/privacy/me').then((r) => r.data.devices),

  /**
   * `startsAt` / `endsAt` are WALL CLOCK, "YYYY-MM-DDTHH:MM", with no timezone
   * suffix — never an ISO instant. The server resolves them against the tenant
   * timezone: this browser cannot (no date-fns-tz), and a driver travelling
   * abroad would otherwise shift their own leave by the offset difference.
   */
  createPeriod: (data: {
    deviceId: string;
    startsAt: string;
    endsAt: string;
    reason: PrivacyReason;
  }) => api.post<PrivacyPeriod>('/privacy/me/periods', data).then((r) => r.data),

  /**
   * Only the end and the reason: moving the start would be retroactive.
   * `endNow` ends the period at the SERVER's clock — "now" cannot be sent as
   * wall clock, since the current minute is already in the past once parsed.
   */
  updatePeriod: (
    periodId: string,
    data: { endsAt?: string; endNow?: true; reason?: PrivacyReason }
  ) =>
    api.patch<PrivacyPeriod>(`/privacy/me/periods/${periodId}`, data).then((r) => r.data),

  /** Rejected by the server once the period has started — shorten it instead. */
  deletePeriod: (periodId: string) => api.delete(`/privacy/me/periods/${periodId}`),

  /** Running declared periods across the fleet, to show WHY a vehicle is private. */
  getActive: () =>
    api
      .get<{ periods: { deviceId: string; reason: PrivacyReason; endsAt: string }[] }>(
        '/privacy/active'
      )
      .then((r) => r.data.periods),
};

export const publicApi = {
  getAppearance: () =>
    axios.get(`${API_URL}/api/public/appearance`).then((res) => res.data),
};

// Share-link API (authenticated dispatcher actions)
export interface ShareLinkCreatePayload {
  deviceId: string;
  missionId?: string | null;
  durationMinutes?: number;
  channel: 'SMS' | 'EMAIL' | 'COPY';
  recipient?: string | null;
  message?: string;
}

export interface ShareLinkCreated {
  id: string;
  url: string;
  token: string;
  expiresAt: string;
  channel: 'SMS' | 'EMAIL' | 'COPY';
  recipient: string | null;
  deviceId: string;
  missionId: string | null;
  delivered: boolean;
  deliveryError: string | null;
}

export const shareLinksApi = {
  create: (payload: ShareLinkCreatePayload) =>
    api.post<ShareLinkCreated>('/share-links', payload).then((r) => r.data),
  list: (params: { deviceId?: string; missionId?: string; active?: boolean }) =>
    api
      .get<{ links: Array<{ id: string; deviceId: string; missionId: string | null; channel: string; recipient: string | null; expiresAt: string; revokedAt: string | null; createdAt: string; lastViewedAt: string | null; viewCount: number; device?: { name: string } }> }>(
        '/share-links',
        {
          params: {
            ...params,
            active: params.active === undefined ? undefined : String(params.active),
          },
        },
      )
      .then((r) => r.data.links),
  revoke: (id: string) => api.delete(`/share-links/${id}`).then((r) => r.data),
};

// Geocoding API via backend (uses local Nominatim + Redis cache)
export interface ForwardGeocodingResult {
  latitude: number;
  longitude: number;
  displayName: string;
  type: string | null;
  importance: number | null;
  boundingBox: [number, number, number, number] | null; // [south, north, west, east]
}

export const geocodingApi = {
  // Forward geocode a query string to candidate locations (used by EV map search)
  search: async (
    q: string,
    opts?: { limit?: number; viewbox?: [number, number, number, number] },
  ): Promise<ForwardGeocodingResult[]> => {
    const params: Record<string, string | number> = { q };
    if (opts?.limit) params.limit = opts.limit;
    if (opts?.viewbox) params.viewbox = opts.viewbox.join(',');
    const r = await api.get<ForwardGeocodingResult[]>('/geocoding/search', { params });
    return r.data;
  },

  // Reverse geocode a single coordinate
  reverse: async (latitude: number, longitude: number) => {
    const response = await api.get(`/geocoding/reverse`, {
      params: { latitude, longitude },
    });
    return {
      data: {
        address: response.data.address || 'Adresse inconnue',
        displayName: response.data.displayName || '',
        components: response.data.components || {},
      },
    };
  },

  // Get short address (city, country)
  short: async (latitude: number, longitude: number) => {
    const response = await api.get(`/geocoding/short`, {
      params: { latitude, longitude },
    });
    return {
      data: { address: response.data.address || 'Lieu inconnu' },
    };
  },

  // Batch geocode multiple coordinates via backend API (with Redis cache)
  batch: async (coordinates: Array<{ lat: number; lng: number }>) => {
    const addresses: Record<string, string> = {};
    let totalPending = 0;

    // Backend limit is 50 coordinates per request — chunk if needed
    const CHUNK_SIZE = 50;
    for (let i = 0; i < coordinates.length; i += CHUNK_SIZE) {
      const chunk = coordinates.slice(i, i + CHUNK_SIZE);
      try {
        const res = await api.post('/geocoding/batch', { coordinates: chunk }, { timeout: 30000 });
        Object.assign(addresses, res.data.addresses);
        totalPending += (res.data.pending || 0);
      } catch {
        // Fallback: use coordinates as display
        for (const coord of chunk) {
          const key = `${coord.lat.toFixed(5)},${coord.lng.toFixed(5)}`;
          addresses[key] = `${coord.lat.toFixed(6)}, ${coord.lng.toFixed(6)}`;
        }
      }
    }

    return {
      data: {
        addresses,
        count: Object.keys(addresses).length,
        requested: coordinates.length,
        pending: totalPending,
      },
    };
  },
};

// ============================================================================
// API Keys
// ============================================================================

export const apiKeysApi = {
  list: () => api.get('/api-keys'),

  create: (data: { name: string; permissions?: string[]; expiresAt?: string }) =>
    api.post('/api-keys', data),

  update: (id: string, data: { name?: string; permissions?: string[]; isActive?: boolean; expiresAt?: string | null }) =>
    api.patch(`/api-keys/${id}`, data),

  rotate: (id: string) => api.post(`/api-keys/${id}/rotate`),

  remove: (id: string) => api.delete(`/api-keys/${id}`),
};

// ============================================================================
// Branding (White-label)
// ============================================================================

export const brandingApi = {
  getStatus: () => api.get('/branding/status'),

  get: () => api.get('/branding'),

  update: (data: {
    logo?: string | null;
    favicon?: string | null;
    primaryColor?: string | null;
    secondaryColor?: string | null;
    appName?: string | null;
    loginBackground?: string | null;
    companyName?: string | null;
    domain?: string | null;
  }) => api.put('/branding', data),
};

// ============================================================================
// IP Security API (Super Admin)
// ============================================================================

export const ipSecurityApi = {
  getStats: () => api.get('/admin/security/stats'),
  getFail2BanConfig: () => api.get('/admin/security/fail2ban'),
  updateFail2BanConfig: (data: { enabled?: boolean; maxAttempts?: number; windowMinutes?: number; banDurationMinutes?: number }) =>
    api.patch('/admin/security/fail2ban', data),
  getGeoConfig: () => api.get('/admin/security/geo'),
  updateGeoConfig: (data: { enabled?: boolean; mode?: 'blocklist' | 'allowlist'; countries?: string[] }) =>
    api.patch('/admin/security/geo', data),
  getWhitelist: () => api.get('/admin/security/whitelist'),
  updateWhitelist: (data: { cidrs: string[] }) => api.patch('/admin/security/whitelist', data),
  getBannedIps: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/admin/security/banned-ips', { params }),
  banIp: (data: { ipAddress: string; description?: string; durationMinutes?: number }) =>
    api.post('/admin/security/banned-ips', data),
  unbanIp: (id: string) => api.delete(`/admin/security/banned-ips/${id}`),
  lookupIp: (ip: string) => api.get(`/admin/security/lookup/${ip}`),
  getMyIp: () => api.get('/admin/security/my-ip'),
};

// ============================================================================
// Beacons API
// ============================================================================

export const beaconApi = {
  list: (params?: {
    page?: number;
    limit?: number;
    type?: string;
    active?: boolean;
    deviceId?: string;
    driverId?: string;
    search?: string;
  }) => api.get('/beacons', { params }),

  getStats: () => api.get('/beacons/stats'),

  get: (id: string) => api.get(`/beacons/${id}`),

  update: (id: string, data: Partial<{
    name: string | null;
    assignedDeviceId: string | null;
    assignedDriverId: string | null;
    active: boolean;
  }>) => api.put(`/beacons/${id}`, data),

  delete: (id: string) => api.delete(`/beacons/${id}`),

  getEvents: (id: string, params?: { from?: string; to?: string; type?: string; limit?: number }) =>
    api.get(`/beacons/${id}/events`, { params }),

  getSensors: (id: string, params?: { from?: string; to?: string; limit?: number }) =>
    api.get(`/beacons/${id}/sensors`, { params }),

  getAllEvents: (params?: { from?: string; to?: string; type?: string; beaconId?: string; deviceId?: string; limit?: number }) =>
    api.get('/beacons/events', { params }),
};

// =============================================================================
// Marketing (SUPER_ADMIN only)
// =============================================================================

export type MarketingPlatform = 'LINKEDIN' | 'FACEBOOK' | 'INSTAGRAM';
export type MarketingPostStatus =
  | 'DRAFT'
  | 'SCHEDULED'
  | 'PUBLISHING'
  | 'PUBLISHED'
  | 'PARTIAL'
  | 'FAILED';

export interface MarketingPost {
  id: string;
  title: string | null;
  content: string;
  mediaUrls: string[];
  platforms: MarketingPlatform[];
  status: MarketingPostStatus;
  scheduledAt: string | null;
  publishedAt: string | null;
  aiGenerated: boolean;
  theme: string | null;
  errorMessage: string | null;
  createdAt: string;
  publications: Array<{
    id: string;
    platform: MarketingPlatform;
    status: 'PENDING' | 'SUCCESS' | 'FAILED';
    externalUrl: string | null;
    error: string | null;
    publishedAt: string | null;
  }>;
  createdBy: { firstName: string; lastName: string; email: string } | null;
}

export interface MarketingFeature {
  id: string;
  slug: string;
  title: string;
  description: string;
  category: string | null;
  sourceUrl: string | null;
}

export interface MarketingAccount {
  id: string;
  platform: MarketingPlatform;
  accountName: string;
  externalAccountId: string;
  expiresAt: string | null;
  scope: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export type MarketingContentType =
  | 'tip'
  | 'case-study'
  | 'product-highlight'
  | 'industry-news'
  | 'question'
  | 'testimonial'
  | 'comparison'
  | 'behind-the-scenes';

export type MarketingTargetAudience =
  | 'fleet-manager'
  | 'transporter'
  | 'artisan'
  | 'pme-director'
  | 'logistics';

export type MarketingEstimatedEngagement = 'low' | 'medium' | 'high';

export interface MarketingGeneratedPost {
  content: string;
  hashtags: string[];
  suggestedPlatforms: MarketingPlatform[];
  title: string;
  callToAction: string;
  estimatedEngagement: MarketingEstimatedEngagement;
  bestPostingTime: string;
  contentType: MarketingContentType;
}

export type MarketingThemeModuleKey =
  | 'VRP'
  | 'POD'
  | 'EV'
  | 'MAINTENANCE'
  | 'FIELD_SERVICE'
  | 'FUEL'
  | 'PRIVACY'
  | 'OEM_CONNECT'
  | 'ASSET_BLE'
  | 'DRIVER_SAFETY'
  | 'GEOFENCING'
  | 'REPORTS';

export interface MarketingThemeSuggestion {
  title: string;
  theme: string;
  contentType: MarketingContentType;
  audience?: MarketingTargetAudience;
}

/** Ready-made AI angles for one product module (see backend theme-catalog.ts) */
export interface MarketingThemeModule {
  key: MarketingThemeModuleKey;
  label: string;
  emoji: string;
  tagline: string;
  facts: string[];
  audiences: MarketingTargetAudience[];
  hashtags: string[];
  suggestions: MarketingThemeSuggestion[];
}

export const marketingApi = {
  // Posts
  listPosts: (params?: { status?: string; platform?: string; limit?: number }) =>
    api.get<MarketingPost[]>('/admin/marketing/posts', { params }).then((r) => r.data),
  getPost: (id: string) =>
    api.get<MarketingPost>(`/admin/marketing/posts/${id}`).then((r) => r.data),
  createPost: (data: {
    title?: string;
    content: string;
    mediaUrls?: string[];
    platforms: MarketingPlatform[];
    scheduledAt?: string | null;
    aiGenerated?: boolean;
    theme?: string | null;
  }) => api.post<MarketingPost>('/admin/marketing/posts', data).then((r) => r.data),
  updatePost: (
    id: string,
    data: Partial<{
      title: string;
      content: string;
      mediaUrls: string[];
      platforms: MarketingPlatform[];
      scheduledAt: string | null;
    }>,
  ) => api.put<MarketingPost>(`/admin/marketing/posts/${id}`, data).then((r) => r.data),
  deletePost: (id: string) => api.delete(`/admin/marketing/posts/${id}`),
  publishPost: (id: string) =>
    api.post(`/admin/marketing/posts/${id}/publish`).then((r) => r.data),

  // AI generation
  generatePost: (data: {
    theme: string;
    platforms: MarketingPlatform[];
    tone?: 'professional' | 'casual' | 'enthusiastic' | 'technical';
    language?: 'fr' | 'en' | 'de';
    withHashtags?: boolean;
    contentType?: MarketingContentType;
    callToAction?: string;
    targetAudience?: MarketingTargetAudience;
    includeEmoji?: boolean;
    moduleKey?: MarketingThemeModuleKey;
  }) =>
    api
      .post<MarketingGeneratedPost>(
        '/admin/marketing/posts/generate',
        data,
      )
      .then((r) => r.data),

  generateCalendar: (data: {
    weekTheme: string;
    numberOfPosts?: number;
    platforms?: MarketingPlatform[];
    language?: 'fr' | 'en' | 'de';
    targetAudience?: MarketingTargetAudience;
    moduleKey?: MarketingThemeModuleKey;
  }) =>
    api
      .post<MarketingGeneratedPost[]>(
        '/admin/marketing/posts/generate-calendar',
        data,
      )
      .then((r) => r.data),

  // Module theme catalogue (ready-made AI angles per product module)
  listThemes: () =>
    api.get<MarketingThemeModule[]>('/admin/marketing/themes').then((r) => r.data),

  // Features (scraped from trackfleet.lu + manual CRUD)
  listFeatures: () =>
    api.get<MarketingFeature[]>('/admin/marketing/features').then((r) => r.data),
  refreshFeatures: () =>
    api.post<{ count: number }>('/admin/marketing/features/refresh').then((r) => r.data),
  scrapeFeaturesPreview: (url: string) =>
    api
      .post<{
        features: Array<{
          slug: string;
          title: string;
          description: string;
          category?: string;
          sourceUrl?: string;
          exists: boolean;
        }>;
        message?: string;
      }>('/admin/marketing/features/scrape-preview', { url })
      .then((r) => r.data),
  createFeature: (data: {
    title: string;
    description: string;
    category?: string | null;
    sourceUrl?: string | null;
  }) =>
    api.post<MarketingFeature>('/admin/marketing/features', data).then((r) => r.data),
  bulkCreateFeatures: (
    features: Array<{
      title: string;
      description: string;
      category?: string | null;
      sourceUrl?: string | null;
    }>,
  ) =>
    api
      .post<{ created: number; skipped: number }>('/admin/marketing/features/bulk', { features })
      .then((r) => r.data),
  updateFeature: (
    id: string,
    data: {
      title?: string;
      description?: string;
      category?: string | null;
      sourceUrl?: string | null;
    },
  ) => api.put<MarketingFeature>(`/admin/marketing/features/${id}`, data).then((r) => r.data),
  deleteFeature: (id: string) => api.delete(`/admin/marketing/features/${id}`),

  // Accounts
  listAccounts: () =>
    api.get<MarketingAccount[]>('/admin/marketing/accounts').then((r) => r.data),
  deleteAccount: (id: string) => api.delete(`/admin/marketing/accounts/${id}`),

  // Media
  uploadMedia: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api
      .post<{ url: string; filename: string; size: number; mimetype: string }>(
        '/admin/marketing/media/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },
  listMedia: () =>
    api
      .get<
        Array<{
          filename: string;
          url: string;
          size: number;
          mimetype: string;
          createdAt: string;
        }>
      >('/admin/marketing/media')
      .then((r) => r.data),
  deleteMedia: (filename: string) =>
    api.delete(`/admin/marketing/media/${encodeURIComponent(filename)}`),

  // Settings (OAuth credentials)
  getSettings: () =>
    api
      .get<{
        status: Record<string, { configured: boolean; source: 'db' | 'env' | 'none' }>;
        masked: Record<string, string | null>;
      }>('/admin/marketing/settings')
      .then((r) => r.data),
  saveSetting: (key: string, value: string) =>
    api.put('/admin/marketing/settings', { key, value }).then((r) => r.data),
  deleteSetting: (key: string) =>
    api.delete(`/admin/marketing/settings/${key}`),
};

// =============================================================================
// Missions (Field Service — requires FIELD_SERVICE module)
// =============================================================================

export type MissionStatus = 'DRAFT' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
export type MissionType = 'INTERVENTION' | 'DELIVERY' | 'PICKUP' | 'INSPECTION' | 'OTHER';

export interface Mission {
  id: string;
  tenantId: string;
  reference: string;
  title: string;
  description: string | null;
  type: MissionType;
  status: MissionStatus;
  address: string;
  city: string | null;
  postalCode: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  geofenceId: string | null;
  clientName: string | null;
  clientContact: string | null;
  clientPhone: string | null;
  clientEmail: string | null;
  scheduledAt: string | null;
  estimatedDurationMin: number | null;
  priority: number;
  assignedDeviceId: string | null;
  assignedDriverId: string | null;
  assignedByUserId: string | null;
  assignedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  completionNotes: string | null;
  attachmentUrls: string[];
  proofPhotoUrls: string[];
  signatureUrl: string | null;
  signerName: string | null;
  /** Cumulative intervention duration in milliseconds — incremented at each
   *  draft save and locked at completion. Final value = total active time
   *  spent on the intervention between start and finalization. */
  interventionDurationMs: number;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  /** Timestamp of the last archive operation; null when the mission is active. */
  archivedAt: string | null;
  assignedDevice: {
    id: string;
    name: string;
    plate: string | null;
    imei: string;
    icon: string;
    markerColor: string;
  } | null;
  assignedDriver: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    email: string | null;
  } | null;
  assignedBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  geofence: { id: string; name: string; type: string } | null;
}

export interface CreateMissionPayload {
  title: string;
  description?: string | null;
  type?: MissionType;
  address: string;
  city?: string | null;
  postalCode?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  geofenceId?: string | null;
  clientName?: string | null;
  clientContact?: string | null;
  clientPhone?: string | null;
  clientEmail?: string | null;
  scheduledAt?: string | null;
  estimatedDurationMin?: number | null;
  priority?: number;
  assignedDeviceId?: string | null;
  assignedDriverId?: string | null;
  attachmentUrls?: string[];
}

export const missionsApi = {
  list: (params?: {
    status?: MissionStatus | 'ALL';
    driverId?: string;
    deviceId?: string;
    from?: string;
    to?: string;
    limit?: number;
    /** 'active' (default) excludes archived ; 'archived' returns only archived ; 'all' returns both. */
    archived?: 'active' | 'archived' | 'all';
  }) => api.get<Mission[]>('/missions', { params }).then((r) => r.data),

  get: (id: string) => api.get<Mission>(`/missions/${id}`).then((r) => r.data),

  create: (data: CreateMissionPayload & { notify?: boolean }) =>
    api.post<Mission>('/missions', data).then((r) => r.data),

  update: (
    id: string,
    data: Partial<CreateMissionPayload> & {
      completionNotes?: string | null;
      signerName?: string | null;
      notify?: boolean;
    },
  ) => api.patch<Mission>(`/missions/${id}`, data).then((r) => r.data),

  delete: (id: string) => api.delete(`/missions/${id}`),

  archive: (id: string) => api.post<Mission>(`/missions/${id}/archive`).then((r) => r.data),
  unarchive: (id: string) => api.post<Mission>(`/missions/${id}/unarchive`).then((r) => r.data),

  assign: (
    id: string,
    data: {
      assignedDeviceId?: string | null;
      assignedDriverId?: string | null;
      notify?: boolean;
    },
  ) => api.post<Mission>(`/missions/${id}/assign`, data).then((r) => r.data),

  start: (id: string) => api.post<Mission>(`/missions/${id}/start`).then((r) => r.data),

  complete: (
    id: string,
    data: {
      notes?: string | null;
      signerName?: string | null;
      signatureUrl?: string | null;
      proofPhotoUrls?: string[];
    } = {},
  ) => api.post<Mission>(`/missions/${id}/complete`, data).then((r) => r.data),

  /**
   * Save in-progress completion data without finalizing the mission.
   * The driver can call this multiple times to add photos/notes/signature
   * progressively. The mission stays IN_PROGRESS until `complete()` is called.
   */
  saveDraft: (
    id: string,
    data: {
      notes?: string | null;
      signerName?: string | null;
      signatureUrl?: string | null;
      proofPhotoUrls?: string[];
    } = {},
  ) => api.post<Mission>(`/missions/${id}/save-draft`, data).then((r) => r.data),

  cancel: (id: string, reason?: string) =>
    api.post<Mission>(`/missions/${id}/cancel`, { reason }).then((r) => r.data),

  listMyToday: (date?: string) =>
    api
      .get<Mission[]>('/missions/me/today', { params: date ? { date } : undefined })
      .then((r) => r.data),

  uploadProof: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api
      .post<{ url: string; filename: string; size: number; mimetype: string }>(
        '/missions/upload-proof',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },

  deleteProof: (filename: string) =>
    api.delete(`/missions/proof/${encodeURIComponent(filename)}`),

  /** Download the mission PDF report as a Blob */
  downloadPdf: (id: string) =>
    api
      .get(`/missions/${id}/pdf`, { responseType: 'blob' })
      .then((r) => r.data as Blob),

  /** Open the mission PDF in a new tab via the browser's PDF viewer.
   *  Returns a Promise that resolves once the tab is opened. */
  viewPdfInBrowser: async (id: string) => {
    const blob = await api
      .get(`/missions/${id}/pdf`, { responseType: 'blob' })
      .then((r) => r.data as Blob);
    const url = window.URL.createObjectURL(
      new Blob([blob], { type: 'application/pdf' }),
    );
    window.open(url, '_blank', 'noopener,noreferrer');
    // Revoke the URL after a short delay so the new tab has time to load it
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  },

  /** Send the mission PDF report to the client by email */
  sendPdf: (id: string, data: { to?: string; subject?: string; message?: string } = {}) =>
    api.post<{ success: boolean; recipient: string }>(`/missions/${id}/send-pdf`, data).then((r) => r.data),
};

// =============================================================================
// POD — Preuve de Livraison (parcels)
// =============================================================================

export type ParcelStatus =
  | 'CREATED'
  | 'ASSIGNED'
  | 'OUT_FOR_DELIVERY'
  | 'DELIVERED'
  | 'PARTIALLY_DELIVERED'
  | 'REFUSED'
  | 'ABSENT'
  | 'RETURNED'
  | 'CANCELLED';

export interface ParcelEvent {
  id: string;
  parcelId: string;
  tenantId: string;
  type: string;
  description: string | null;
  lat: number | null;
  lng: number | null;
  actorUserId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface Parcel {
  id: string;
  tenantId: string;
  reference: string;
  orderReference: string | null;
  barcode: string | null;
  barcodeFormat: string | null;
  status: ParcelStatus;
  recipientName: string | null;
  recipientPhone: string | null;
  recipientEmail: string | null;
  recipientAddress: string | null;
  recipientCity: string | null;
  recipientPostalCode: string | null;
  recipientCountry: string | null;
  recipientLat: number | null;
  recipientLng: number | null;
  senderName: string | null;
  senderEmail: string | null;
  senderAddress: string | null;
  senderCity: string | null;
  senderPostalCode: string | null;
  senderCountry: string | null;
  receivedAt: string | null;
  weightKg: number | null;
  notes: string | null;
  scheduledDate: string | null;
  scheduledWindowStart: string | null;
  scheduledWindowEnd: string | null;
  assignedDriverId: string | null;
  assignedDeviceId: string | null;
  assignedByUserId: string | null;
  assignedAt: string | null;
  missionId: string | null;
  dispatchScannedAt: string | null;
  deliveredAt: string | null;
  deliveredLat: number | null;
  deliveredLng: number | null;
  signatureUrl: string | null;
  signerName: string | null;
  photoUrls: string[];
  reserves: string | null;
  failureReason: string | null;
  importProfileId: string | null;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt: string | null;
  assignedDevice: {
    id: string;
    name: string;
    plate: string | null;
    imei: string;
    icon: string;
    markerColor: string;
  } | null;
  assignedDriver: {
    id: string;
    firstName: string;
    lastName: string;
    phone: string | null;
    email: string | null;
  } | null;
  assignedBy: { id: string; firstName: string; lastName: string; email: string } | null;
  createdBy: { id: string; firstName: string; lastName: string; email: string } | null;
  events: ParcelEvent[];
}

export interface CreateParcelPayload {
  orderReference?: string | null;
  barcode?: string | null;
  barcodeFormat?: string | null;
  recipientName?: string | null;
  recipientPhone?: string | null;
  recipientEmail?: string | null;
  recipientAddress?: string | null;
  recipientCity?: string | null;
  recipientPostalCode?: string | null;
  recipientCountry?: string | null;
  recipientLat?: number | null;
  recipientLng?: number | null;
  senderName?: string | null;
  senderEmail?: string | null;
  senderAddress?: string | null;
  senderCity?: string | null;
  senderPostalCode?: string | null;
  senderCountry?: string | null;
  receivedAt?: string | null;
  weightKg?: number | null;
  notes?: string | null;
  scheduledDate?: string | null;
  scheduledWindowStart?: string | null;
  scheduledWindowEnd?: string | null;
  assignedDriverId?: string | null;
  assignedDeviceId?: string | null;
}

export type ParcelImportColumnRef =
  | { kind: 'column'; ref: string | number }
  | { kind: 'constant'; value: string };

export type ParcelImportField =
  | 'orderReference' | 'barcode' | 'barcodeFormat'
  | 'recipientName' | 'recipientPhone' | 'recipientEmail'
  | 'recipientAddress' | 'recipientCity' | 'recipientPostalCode' | 'recipientCountry'
  | 'senderName' | 'senderEmail' | 'senderAddress'
  | 'receivedAt' | 'weightKg' | 'scheduledDate' | 'notes';

export interface ParcelImportConfig {
  source: string;
  fileFormat: 'csv' | 'xlsx';
  separator: ';' | ',' | '\t' | '|';
  encoding: 'utf-8' | 'utf-8-bom' | 'iso-8859-1';
  decimalSeparator: '.' | ',';
  dateFormat: 'yyyy-MM-dd' | 'dd/MM/yyyy' | 'dd.MM.yyyy' | 'MM/dd/yyyy';
  hasHeader: boolean;
  startRow: number;
  columnMapping: Partial<Record<ParcelImportField, ParcelImportColumnRef>>;
}

export interface ParcelImportProfile {
  id: string;
  name: string;
  config: ParcelImportConfig;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ParcelInspectResult {
  headers: string[];
  preview: string[][];
  totalRows: number;
}

export interface PublicParcelTracking {
  reference: string;
  status: ParcelStatus;
  recipientName: string | null;
  city: string | null;
  scheduledDate: string | null;
  deliveredAt: string | null;
  signerName: string | null;
  carrier: string | null;
  events: Array<{ type: string; at: string }>;
}

export interface ParcelImportSummary {
  totalRows: number;
  created: number;
  updated: number;
  skippedInvalid: number;
  errors: Array<{ row: number; reason: string }>;
}

export const PARCEL_IMPORT_FIELDS: ParcelImportField[] = [
  'orderReference', 'barcode', 'barcodeFormat',
  'recipientName', 'recipientPhone', 'recipientEmail',
  'recipientAddress', 'recipientCity', 'recipientPostalCode', 'recipientCountry',
  'senderName', 'senderEmail', 'senderAddress',
  'receivedAt', 'weightKg', 'scheduledDate', 'notes',
];

export const parcelsApi = {
  list: (params?: {
    status?: ParcelStatus | 'ALL';
    driverId?: string;
    deviceId?: string;
    search?: string;
    from?: string;
    to?: string;
    limit?: number;
    archived?: 'active' | 'archived' | 'all';
  }) => api.get<Parcel[]>('/parcels', { params }).then((r) => r.data),

  get: (id: string) => api.get<Parcel>(`/parcels/${id}`).then((r) => r.data),

  create: (data: CreateParcelPayload & { notify?: boolean }) =>
    api.post<Parcel>('/parcels', data).then((r) => r.data),

  update: (id: string, data: Partial<CreateParcelPayload>) =>
    api.patch<Parcel>(`/parcels/${id}`, data).then((r) => r.data),

  delete: (id: string) => api.delete(`/parcels/${id}`),

  assign: (
    id: string,
    data: {
      assignedDriverId?: string | null;
      assignedDeviceId?: string | null;
      scheduledDate?: string | null;
      notify?: boolean;
    },
  ) => api.post<Parcel>(`/parcels/${id}/assign`, data).then((r) => r.data),

  cancel: (id: string, reason?: string) =>
    api.post<Parcel>(`/parcels/${id}/cancel`, { reason }).then((r) => r.data),

  /** Assign several parcels to a driver/vehicle at once. */
  bulkAssign: (
    ids: string[],
    data: {
      assignedDriverId?: string | null;
      assignedDeviceId?: string | null;
      scheduledDate?: string | null;
      notify?: boolean;
    },
  ) => api.post<{ updated: number }>('/parcels/bulk-assign', { ids, ...data }).then((r) => r.data),

  archive: (id: string) => api.post<Parcel>(`/parcels/${id}/archive`).then((r) => r.data),
  unarchive: (id: string) => api.post<Parcel>(`/parcels/${id}/unarchive`).then((r) => r.data),

  markOutForDelivery: (id: string) =>
    api.post<Parcel>(`/parcels/${id}/out-for-delivery`).then((r) => r.data),

  deliver: (
    id: string,
    data: {
      outcome?: 'DELIVERED' | 'PARTIALLY_DELIVERED';
      signerName?: string | null;
      signatureUrl?: string | null;
      photoUrls?: string[];
      reserves?: string | null;
      lat?: number | null;
      lng?: number | null;
      scannedBarcode?: string | null;
    },
  ) => api.post<Parcel>(`/parcels/${id}/deliver`, data).then((r) => r.data),

  fail: (
    id: string,
    data: {
      outcome: 'REFUSED' | 'ABSENT' | 'RETURNED';
      reason?: string | null;
      lat?: number | null;
      lng?: number | null;
    },
  ) => api.post<Parcel>(`/parcels/${id}/fail`, data).then((r) => r.data),

  listMyToday: (date?: string) =>
    api
      .get<Parcel[]>('/parcels/me/today', { params: date ? { date } : undefined })
      .then((r) => r.data),

  /** Resolve a scanned barcode to the parcel it identifies. */
  lookup: (barcode: string) =>
    api.get<Parcel>('/parcels/lookup', { params: { barcode } }).then((r) => r.data),

  uploadProof: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api
      .post<{ url: string; filename: string; size: number; mimetype: string }>(
        '/parcels/upload-proof',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((r) => r.data);
  },

  deleteProof: (filename: string) =>
    api.delete(`/parcels/proof/${encodeURIComponent(filename)}`),

  /** (Re)issue the public tracking token; returns the plaintext token + path once. */
  issueTrackingToken: (id: string) =>
    api.post<{ token: string; path: string }>(`/parcels/${id}/tracking-token`).then((r) => r.data),

  /** Open the bon de livraison PDF in a new tab. */
  viewPdfInBrowser: async (id: string) => {
    const blob = await api.get(`/parcels/${id}/pdf`, { responseType: 'blob' }).then((r) => r.data as Blob);
    const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  },
  /** (Re)send the bon de livraison to recipient + sender. */
  sendPod: (id: string) =>
    api.post<{ recipients: string[] }>(`/parcels/${id}/send-pod`).then((r) => r.data),

  /** Email the recipient a public tracking link with the planned delivery date. */
  sendTrackingEmail: (id: string) =>
    api.post<{ recipient: string; url: string }>(`/parcels/${id}/send-tracking-email`).then((r) => r.data),

  /** Public, unauthenticated parcel tracking by token (for the recipient page). */
  publicTrack: (token: string) =>
    api.get<PublicParcelTracking>(`/public/parcel/track/${token}`).then((r) => r.data),

  // --- Import (CSV/XLSX) with per-source profiles ---
  listImportProfiles: () =>
    api.get<{ profiles: ParcelImportProfile[]; activeProfileId: string | null }>('/parcels/import-profiles').then((r) => r.data),
  createImportProfile: (name: string, config?: ParcelImportConfig) =>
    api.post<ParcelImportProfile>('/parcels/import-profiles', { name, config }).then((r) => r.data),
  updateImportProfile: (id: string, data: { name?: string; config?: ParcelImportConfig }) =>
    api.patch<ParcelImportProfile>(`/parcels/import-profiles/${id}`, data).then((r) => r.data),
  deleteImportProfile: (id: string) => api.delete(`/parcels/import-profiles/${id}`),
  activateImportProfile: (id: string) =>
    api.post<ParcelImportProfile>(`/parcels/import-profiles/${id}/activate`).then((r) => r.data),
  getImportTemplate: (source: string) =>
    api.get<ParcelImportConfig>(`/parcels/import-templates/${source}`).then((r) => r.data),
  inspectImport: (
    file: File,
    opts: { fileFormat: string; encoding: string; separator: string; hasHeader: boolean; startRow: number },
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    Object.entries(opts).forEach(([k, v]) => fd.append(k, String(v)));
    return api
      .post<ParcelInspectResult>('/parcels/import/inspect', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
  runImport: (file: File, profileId: string) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('profileId', profileId);
    return api
      .post<ParcelImportSummary>('/parcels/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => r.data);
  },
};

// =============================================================================
// EV — Electric Vehicle module
// =============================================================================

export interface EvModel {
  id: string;
  make: string;
  model: string;
  variant: string | null;
  yearFrom: number | null;
  yearTo: number | null;
  vehicleType: 'BEV' | 'PHEV';
  batteryKwh: number;
  rangeWltpKm: number;
  acChargerKw: number | null;
  dcChargerKw: number | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EvModelInput {
  make: string;
  model: string;
  variant?: string | null;
  yearFrom?: number | null;
  yearTo?: number | null;
  vehicleType: 'BEV' | 'PHEV';
  batteryKwh: number;
  rangeWltpKm: number;
  acChargerKw?: number | null;
  dcChargerKw?: number | null;
  active?: boolean;
}

export interface EvDeviceSnapshot {
  deviceId: string;
  name: string;
  plate: string | null;
  evModelId: string | null;
  evModel: {
    id: string;
    make: string;
    model: string;
    variant: string | null;
    batteryKwh: number;
    rangeWltpKm: number;
    vehicleType: 'BEV' | 'PHEV';
  } | null;
  currentSoc: number | null;
  currentChargingState: number | null;
  estimatedRangeKm: number | null;
  socObservedAt: string | null;
}

export interface EvFleetSummary {
  totalEvDevices: number;
  withRecentSoc: number;
  currentlyCharging: number;
  lowSocCount: number;
  avgSoc: number | null;
  totalEstimatedRangeKm: number | null;
}

export interface EvSocPoint {
  timestamp: string;
  soc: number;
  chargingState: number | null;
}

export interface EvChargeSession {
  id: string;
  tenantId: string;
  deviceId: string;
  startedAt: string;
  endedAt: string | null;
  startSoc: number | null;
  endSoc: number | null;
  energyKwh: number | null;
  startLatitude: number | null;
  startLongitude: number | null;
  geocodedAddress: string | null;
  interrupted: boolean;
}

export interface EvChargePoint {
  id: number;
  uuid: string;
  name: string;
  address: string | null;
  city: string | null;
  postcode: string | null;
  latitude: number;
  longitude: number;
  operator: string | null;
  numberOfPoints: number | null;
  status: string | null;
  isOperational: boolean | null;
  maxPowerKw: number | null;
  connections: Array<{
    typeId: number;
    typeName: string | null;
    powerKw: number | null;
    quantity: number | null;
  }>;
}

export const evApi = {
  /** Tenant-facing autocomplete on the EV catalog (active models by default) */
  listModels: (params?: { q?: string; activeOnly?: boolean }) =>
    api.get<EvModel[]>('/ev/models', { params }).then((r) => r.data),

  /** Fleet dashboard data: KPIs + per-device snapshots */
  getFleet: () =>
    api
      .get<{ summary: EvFleetSummary; devices: EvDeviceSnapshot[] }>('/ev/fleet')
      .then((r) => r.data),

  /** Single-device current state (model, SoC, range) */
  getDevice: (id: string) =>
    api.get<EvDeviceSnapshot>(`/ev/devices/${id}`).then((r) => r.data),

  /** SoC chart data (default 7 days, downsampled) */
  getSocTimeline: (id: string, days = 7) =>
    api
      .get<EvSocPoint[]>(`/ev/devices/${id}/soc-timeline`, { params: { days } })
      .then((r) => r.data),

  /** Charge session history for one device */
  listSessions: (id: string, limit = 50) =>
    api
      .get<EvChargeSession[]>(`/ev/devices/${id}/sessions`, { params: { limit } })
      .then((r) => r.data),

  /**
   * Public charging stations within a bounding box, sourced from OpenChargeMap
   * (server caches 24h per rounded bbox).
   */
  listChargePoints: (
    bbox: { swLat: number; swLng: number; neLat: number; neLng: number },
    filters?: { minPowerKw?: number; connectionTypeId?: number },
  ) =>
    api
      .get<EvChargePoint[]>('/ev/charge-points', {
        params: {
          bbox: `${bbox.swLat},${bbox.swLng},${bbox.neLat},${bbox.neLng}`,
          ...(filters?.minPowerKw ? { minPowerKw: filters.minPowerKw } : {}),
          ...(filters?.connectionTypeId ? { connectionTypeId: filters.connectionTypeId } : {}),
        },
      })
      .then((r) => r.data),
};

// ---------------------------------------------------------------------------
// VRP module — Optimisation de Tournées
// ---------------------------------------------------------------------------

export interface VrpStop {
  id: string;
  sequence: number;
  missionId: string | null;
  parcelId: string | null;
  label: string | null;
  latitude: number;
  longitude: number;
  address: string | null;
  serviceDurationS: number;
  twStart: string | null;
  twEnd: string | null;
  plannedArrival: string | null;
  plannedDeparture: string | null;
  arrivalDistanceM: number | null;
  arrivalDurationS: number | null;
  demand: number;
  requiredSkills: string[];
  isBreak: boolean;
  mission?: { id: string; reference: string; title: string; status: string } | null;
  parcel?: { id: string; reference: string; recipientName: string | null; status: string } | null;
}

export interface VrpTour {
  id: string;
  date: string;
  status: 'PLANNED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  vehicleId: string | null;
  driverId: string | null;
  startLatitude: number | null;
  startLongitude: number | null;
  endLatitude: number | null;
  endLongitude: number | null;
  totalDistanceM: number;
  totalDurationS: number;
  totalServiceS: number;
  totalWaitingS: number;
  geometry: { polyline?: string } | null;
  summary: { cost?: number; steps?: number; unassigned?: number } | null;
  createdAt: string;
  vehicle: { id: string; name: string; plate: string | null; markerColor: string | null } | null;
  driver: { id: string; firstName: string; lastName: string } | null;
  stops: VrpStop[];
}

export interface VrpOptimizeRequest {
  date: string; // YYYY-MM-DD
  missionIds?: string[];
  stops?: Array<{
    missionId?: string;
    parcelId?: string;
    label?: string;
    latitude: number;
    longitude: number;
    address?: string;
    serviceDurationS?: number;
    twStart?: string;
    twEnd?: string;
    demand?: number;
    requiredSkills?: string[];
  }>;
  vehicles: Array<{
    vehicleId: string;
    driverId?: string;
    startLatitude: number;
    startLongitude: number;
    endLatitude?: number;
    endLongitude?: number;
    shiftStart?: string;
    shiftEnd?: string;
    capacity?: number;
    skills?: string[];
    lockedSequenceIdx?: number[];
  }>;
}

export interface VrpOptimizeResponse {
  tours: VrpTour[];
  warnings?: { failedRefs: string[]; approximateRefs: string[] };
}

export const vrpApi = {
  optimize: (body: VrpOptimizeRequest) =>
    api.post<VrpOptimizeResponse>('/vrp/optimize', body).then((r) => r.data),

  listTours: (params?: { from?: string; to?: string }) =>
    api.get<{ tours: VrpTour[] }>('/vrp/tours', { params }).then((r) => r.data.tours),

  /** Driver-scoped: only the calling user's tours, optionally for one date. */
  listMyTours: (params?: { date?: string }) =>
    api.get<{ tours: VrpTour[] }>('/vrp/me/tours', { params }).then((r) => r.data.tours),

  /** Drivers + their primary vehicle (depot, capacity, skills). Used by the dispatcher UI. */
  listDrivers: () =>
    api.get<{ drivers: VrpDriver[] }>('/vrp/drivers').then((r) => r.data.drivers),

  getTour: (id: string) =>
    api.get<{ tour: VrpTour }>(`/vrp/tours/${id}`).then((r) => r.data.tour),

  deleteTour: (id: string) =>
    api.delete(`/vrp/tours/${id}`).then((r) => r.data),
};

export interface VrpDriver {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  missionsEnabled: boolean;
  vehicle: {
    id: string;
    name: string;
    plate: string | null;
    depotLatitude: number | null;
    depotLongitude: number | null;
    capacityKg: number | null;
    skills: string[];
    lastPosition: { lat?: number; lng?: number } | null;
  } | null;
}

export interface OemStatus {
  provider: string;
  connected: boolean;
  status: string | null;
  linkedVehicles: number;
}

export interface OemVehicle {
  oemVehicleId: string;
  vin?: string;
  brand?: string;
  model?: string;
  year?: number;
  plate?: string;
}

export interface OemLinkInput {
  oemVehicleId: string;
  vin?: string;
  brand?: string;
  model?: string;
  year?: number;
  plate?: string;
  name?: string;
}

// OEM Connect (manufacturer telematics). All endpoints require the OEM_CONNECT module.
export const oemApi = {
  getStatus: () => api.get<OemStatus>('/oem/status').then((r) => r.data),

  connect: (redirectUri: string) =>
    api.post<{ authorizeUrl: string }>('/oem/connect', { redirectUri }).then((r) => r.data),

  callback: (code: string, state: string) =>
    api.post<{ success: boolean; provider: string }>('/oem/callback', { code, state }).then((r) => r.data),

  getVehicles: () =>
    api.get<{ vehicles: OemVehicle[] }>('/oem/vehicles').then((r) => r.data.vehicles),

  linkVehicle: (payload: OemLinkInput) =>
    api.post<{ deviceId: string; created: boolean }>('/oem/vehicles/link', payload).then((r) => r.data),

  disconnect: () => api.post('/oem/disconnect').then((r) => r.data),
};

export default api;
